import { get, isArray, isNil, isPlainObject } from 'lodash';

import { Alias } from '../marketplace-skus/marketplace-skus.types';
import { ChangePasswordOptions } from '@/services/users/users.types';
import { Cage, CageCarrierLabel } from '../cages/cages.types';
import { Commission } from '../commissions/commissions.types';
import { Collection, CollectionName } from '../collections/collections.types';
import { DataExportFilters } from '../data-exports/data-exports.types';
import { Device, RelocateOptions } from '../devices/devices.types';
import { dicoLabelsService } from '../dico-labels/dico-labels.service';
import { GlobalSettings } from '../global-settings/global-settings.types';
import { MarketplaceSkuImportOptions } from '../marketplace-skus/marketplace-skus.types';
import {
  ReportMatchErrorOptions,
  SkuDifferenceCostImportOptions,
  ImportUpdateStatusOptions
} from '../matches/matches.types';
import { FilterOptions } from '../options/options.types';
import { OrderLine } from '../order-lines/order-lines.types';
import {
  OrderReturn,
  OrderReturnExchangeOptions,
  OrderReturnReceptionOptions
} from '../order-returns/order-returns.types';
import { Order, OrderAddress, OrdersImportOptions } from '../orders/orders.types';
import { Picking } from '../pickings/pickings.types';
import { ProductionOrder } from '../production-orders/production-orders.types';
import { Purchase, PurchaseDealOptions, PurchasePauseOptions } from '../purchases/purchases.types';
import { StockStatus } from '../stocks-status/stocks-status.types';
import { suppliersService } from '../suppliers/suppliers.service';
import { referencesService } from '../references/references.service';
import { Supplier, SupplierRib } from '../suppliers/suppliers.types';
import { FormControlOption, FormControlRaw } from './form-control.types';
import { Form } from './form.class';
import { stocksStatusService } from '../stocks-status/stocks-status.service';
import { Marketplace } from '../marketplaces/marketplaces.types';
import { Part, PartType } from '../parts/parts.types';
import { purchasesService } from '../purchases/purchases.service';
import { buybackService } from '../buybacks/buybacks.service';
import { datesService } from '../dates/dates.service';
import { devicesService } from '../devices/devices.service';
import { PURCHASE_STATUS_LABELS } from '../entry-flow-stats/entry-flow-stats.types';
import { SupplierGrade } from '../suppliers-grade/suppliers-grade.types';
import { RepairCostImportOptions } from '../repair-costs/repair-costs.types';
import { SupplierContact } from '../supplier-contacts/supplier-contacts.types';
import { Task } from '../task/tasks.types';
import { LocationBatchWithdrawalOptions } from '../locations/locations.types';
import { Buyback } from '../buybacks/buybacks.types';

type DeviceFormType = 'control' | 'control-relocate' | 'device' | 'device-creation';
type OrderLineFormType = 'order-line' | 'order-line-creation' | 'order-line-exchange-creation';
type OrderFormType = 'order' | 'order-creation';
type OrderReturnFormType = 'order-return' | 'order-return-creation';
type ProductionOrderLineFormType = 'production-order' | 'production-order-line-creation';
type PickingFormType = 'picking' | 'picking-creation';
type CageFormType = 'cage' | 'cage-creation';
type CommissionFormType = 'commission' | 'commission-creation';
type MarketplaceFormType = 'marketplace';
type AliasFormType = 'alias';
type PartFormType = 'part';
type BuybackFormType = 'buyback';
type FormType =
  | DeviceFormType
  | OrderLineFormType
  | OrderFormType
  | OrderReturnFormType
  | ProductionOrderLineFormType
  | PickingFormType
  | CageFormType
  | CommissionFormType
  | MarketplaceFormType
  | AliasFormType
  | PartFormType
  | BuybackFormType
  | 'purchase'
  | 'supplier'
  | 'supplier-display'
  | 'supplier-creation'
  | 'purchase-reception'
  | 'purchase-creation'
  | 'cage-carrier-label-display'
  | 'supplier-contact'
  | 'supplier-grade'
  | 'task-creation';

class FormsService {
  getForm(form: DeviceFormType, value: Device, globalSettings: GlobalSettings): Form<Device>;
  getForm(
    form: OrderLineFormType,
    value: OrderLine,
    globalSettings: GlobalSettings,
    options?
  ): Form<OrderLine & OrderAddress>;
  getForm(form: OrderFormType, value: Order, globalSettings: GlobalSettings, options?): Form<Order & OrderAddress>;
  getForm(
    form: OrderReturnFormType,
    value: OrderReturn,
    globalSettings: GlobalSettings,
    options?
  ): Form<OrderReturn & OrderAddress>;
  getForm(
    form: ProductionOrderLineFormType,
    value: ProductionOrder,
    globalSettings: GlobalSettings,
    options?
  ): Form<ProductionOrder>;
  getForm(form: CageFormType, value: Cage, globalSettings: GlobalSettings, options?): Form<Cage>;
  getForm(form: PickingFormType, value: Picking, globalSettings: GlobalSettings, options?): Form<Picking>;
  getForm(form: PartFormType, value: Part, globalSettings: GlobalSettings, options?): Form<Part>;
  getForm(form: 'purchase-reception', value: Purchase, globalSettings: GlobalSettings): Form<Purchase>;
  getForm(form: 'purchase', value: Purchase, globalSettings: GlobalSettings): Form<Purchase>;
  getForm(form: 'supplier', value: Supplier, globalSettings: GlobalSettings): Form<Supplier>;
  getForm(form: 'supplier-display', value: Supplier, globalSettings: GlobalSettings): Form<Supplier>;
  getForm(form: 'supplier-creation', value: Supplier, globalSettings: GlobalSettings): Form<Supplier>;
  getForm(form: 'purchase-creation', value: Purchase, globalSettings: GlobalSettings): Form<Purchase>;
  getForm(form: 'supplier-contact', value: SupplierContact, globalSettings: GlobalSettings): Form<SupplierContact>;
  getForm(form: 'supplier-grade', value: SupplierGrade, globalSettings: GlobalSettings, options?): Form<SupplierGrade>;
  getForm(form: 'task-creation', value: Task, globalSettings: GlobalSettings): Form<Task>;
  getForm(
    form: 'cage-carrier-label-display',
    value: CageCarrierLabel,
    globalSettings: GlobalSettings
  ): Form<CageCarrierLabel>;
  getForm(form: CommissionFormType, value: Commission, globalSettings: GlobalSettings, options?): Form<Commission>;
  getForm(form: MarketplaceFormType, value: Marketplace, globalSettings: GlobalSettings, options?): Form<Marketplace>;
  getForm(form: AliasFormType, value: Alias, globalSettings: GlobalSettings, options?): Form<Alias>;
  getForm(form: BuybackFormType, value: Buyback, globalSettings: GlobalSettings, options?): Form<Buyback>;
  getForm(form: FormType, value: any, globalSettings: GlobalSettings, options?): Form<any> {
    if (!value) return null;
    if (form === 'device-creation' || form === 'control' || form === 'device')
      return this.getDeviceForm(value, form, globalSettings);

    if (form === 'alias') return this.getAliasForm(value, globalSettings, options);
    if (form === 'buyback') return this.getBuybackForm(value, globalSettings);
    if (form === 'cage') return this.getCageForm();
    if (form === 'cage-carrier-label-display') return this.getCageCarrierLabelDisplayForm(value, globalSettings);
    if (form === 'cage-creation') return this.getCageCreationForm(value, globalSettings);
    if (form === 'commission') return this.getCommissionForm(value);
    if (form === 'commission-creation') return this.getCommissionCreationForm(value, globalSettings, options);
    if (form === 'control-relocate') return this.getControlRelocateForm();
    if (form === 'marketplace') return this.getMarketplaceForm(value, globalSettings, options);
    if (form === 'order') return this.getOrderForm(value, globalSettings, options);
    if (form === 'order-creation') return this.getOrderCreationForm(value, globalSettings, options);
    if (form === 'order-line') return this.getOrderLineForm(value, globalSettings, options);
    if (form === 'order-line-creation') return this.getOrderLineCreationForm(value, globalSettings);
    if (form === 'order-line-exchange-creation') return this.getOrderLineExchangeCreationForm(value, globalSettings);
    if (form === 'order-return') return this.getOrderReturnForm(value, globalSettings, options);
    if (form === 'order-return-creation') return this.getOrderReturnCreationForm(value, globalSettings, options);
    if (form === 'part') return this.getPartForm(value, globalSettings);
    if (form === 'picking') return this.getPickingForm(value);
    if (form === 'picking-creation') return this.getPickingCreationForm(value);
    if (form === 'production-order') return this.getProductionOrderForm(value);
    if (form === 'production-order-line-creation') return this.getProductionOrderCreationForm(value);
    if (form === 'purchase') return this.getPurchaseForm(value, globalSettings);
    if (form === 'purchase-creation') return this.getPurchaseCreationForm(value, globalSettings);
    if (form === 'purchase-reception') return this.getPurchaseReceptionForm(value);
    if (form === 'supplier') return this.getSupplierForm(value, globalSettings);
    if (form === 'supplier-contact') return this.getSupplierContactForm(value);
    if (form === 'supplier-creation') return this.getSupplierCreationForm(value, globalSettings);
    if (form === 'supplier-display') return this.getSupplierDisplayForm(value, globalSettings);
    if (form === 'supplier-grade') return this.getSupplierGradeForm(value, options, globalSettings);
    if (form === 'task-creation') return this.getTaskCreationForm(value);
  }

  getFiltersForm<T extends CollectionName>(
    values: FilterOptions<T>,
    filters: FilterOptions<T>,
    settings: GlobalSettings
  ) {
    if (!filters || !settings) return null;
    const form = new Form<Collection<T>>([
      {
        name: 'filters',
        size: 'medium',
        controls: Object.keys(filters).map(option => {
          if (isArray(filters[option])) {
            const value = isNil(values[option]) ? ['all'] : isArray(values[option]) ? values[option] : [values[option]];
            return {
              name: option,
              label: dicoLabelsService.getCollectionLabel(option, 'long'),
              type: 'multi-dropdown',
              options: this.getFilterOptions(option, filters[option], settings),
              value
            };
          } else if (isPlainObject(filters[option])) {
            const { min, max, init } = filters[option];
            const value = isNil(values[option]) ? init : values[option];
            return {
              name: option,
              label: dicoLabelsService.getCollectionLabel(option, 'long'),
              type: 'slider',
              min,
              max,
              value
            };
          } else throw new Error('Format de filtre invalide');
        })
      }
    ]);

    return form;
  }

  getBuybackForm(buyback: Buyback, globalSettings: GlobalSettings): Form<Buyback> {
    return new Form<Buyback>([
      {
        name: 'device',
        label: '<i class="fas fa-laptop"></i> Ordinateur',
        controls: [
          {
            name: 'sku_okamac',
            label: dicoLabelsService.getCollectionLabel('sku_okamac', 'short'),
            type: 'text',
            value: buyback.sku_okamac,
            split: 2
          },
          {
            name: 'sku_marketplace',
            label: dicoLabelsService.getCollectionLabel('sku_marketplace', 'short'),
            type: 'text',
            value: buyback.sku_marketplace,
            split: 2
          },
          {
            name: 'description',
            label: dicoLabelsService.getCollectionLabel('ordered_item_desc'),
            type: 'text',
            value: buyback.description,
            split: 1
          },
          {
            name: 'marketplace_grade',
            label: dicoLabelsService.getCollectionLabel('marketplace_grade'),
            type: 'text',
            value: buyback.marketplace_grade,
            split: 2
          },
          {
            name: 'price',
            label: dicoLabelsService.getCollectionLabel('price', 'long'),
            unit: dicoLabelsService.getUnit('money'),
            type: 'number',
            value: buyback.price,
            split: 2
          },
          {
            name: 'commission_price',
            label: 'Prix commission',
            unit: dicoLabelsService.getUnit('money'),
            type: 'number',
            value: buyback.commission_price,
            disabled: true,
            split: 2
          },
          ...(buyback.rma_to_do
            ? ([
                {
                  name: 'price_counter_offer',
                  label: 'Prix contre offre',
                  unit: dicoLabelsService.getUnit('money'),
                  type: 'number',
                  value: buyback.price_counter_offer,
                  split: 2
                }
              ] as FormControlRaw<Buyback>[])
            : [])
        ]
      },
      ...(buyback.device
        ? [
            {
              name: 'device_details',
              label: '<i class="fas fa-hdd"></i> Device associe',
              controls: [
                {
                  name: 'device.serial_no',
                  label: dicoLabelsService.getCollectionLabel('serial_no', 'short'),
                  type: 'text',
                  value: buyback.device.serial_no,
                  split: 2
                },
                {
                  name: 'device.sku',
                  label: dicoLabelsService.getCollectionLabel('sku_okamac', 'short'),
                  type: 'text',
                  value: buyback.device.sku,
                  split: 2
                }
              ] as FormControlRaw<Buyback>[]
            }
          ]
        : []),
      {
        name: 'reception',
        label: '<i class="fas fa-box-open"></i> Reception',
        controls: [
          {
            name: 'box',
            label: 'Boite',
            type: 'toggle',
            value: buyback.box,
            split: 3
          },
          {
            name: 'charger',
            label: 'Chargeur',
            type: 'toggle',
            value: buyback.charger,
            split: 3
          },
          {
            name: 'other_item',
            label: 'Autres produits',
            type: 'number',
            value: buyback.other_item,
            split: 3
          },
          {
            name: 'other_item_description',
            label: 'Description des autres produits',
            type: 'text-multiline',
            value: buyback.other_item_description,
            split: 1
          }
        ] as FormControlRaw<Buyback>[]
      },
      ...(buyback.bm_status === 'CANCELED'
        ? [
            {
              name: 'return_shipping',
              label: '<i class="fas fa-truck"></i> Expedition retour',
              controls: [
                {
                  name: 'return_tracking_number',
                  label: 'Numero de tracking',
                  type: 'text',
                  value: buyback.return_tracking_number,
                  disabled: true,
                  split: 2
                },
                {
                  name: 'manual_tracking_number',
                  label: 'Numero de tracking manuel',
                  type: 'text',
                  value: buyback.manual_tracking_number,
                  split: 2
                },
                {
                  name: 'return_label_url',
                  label: 'Lien du BL',
                  type: 'text',
                  value: buyback.return_label_url,
                  disabled: true,
                  split: 1
                }
              ] as FormControlRaw<Buyback>[]
            }
          ]
        : []),
      {
        name: 'shipping_info',
        label: '<i class="fas fa-user-alt"></i> Information client',
        controls: [
          {
            name: 'shipping_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(buyback, 'shipping_info.last_name'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(buyback, 'shipping_info.first_name'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(buyback, 'shipping_info.phone'),
            split: 3
          },
          {
            name: 'shipping_info.address1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(buyback, 'shipping_info.address1'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.address2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(buyback, 'shipping_info.address2'),
            split: 2
          },
          {
            name: 'shipping_info.zip_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(buyback, 'shipping_info.zip_code'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(buyback, 'shipping_info.city'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(buyback, 'shipping_info.country'),
            required: true,
            split: 3
          }
        ]
      }
    ]);
  }

  getReportMatchErrorForm(
    device: Device,
    globalSettings: GlobalSettings,
    options: {
      disableStockDropdown?: boolean;
      disableFutureSteps?: boolean;
      stocksSubSet?: [number, StockStatus][];
      is_manager?: boolean;
    }
  ): Form<Omit<ReportMatchErrorOptions, 'dissociate'>> {
    return new Form<Omit<ReportMatchErrorOptions, 'dissociate'>>([
      {
        name: 'report-match-error',
        controls: [
          ...this.getRelocateControls(device, globalSettings, options),
          {
            name: 'comment',
            label: 'Commentaire',
            type: 'text-multiline',
            required: true
          }
        ]
      }
    ]);
  }

  getRelocateForm(
    init: { stockId?: string | number; stepCode?: string; matched: boolean },
    device: Device,
    globalSettings: GlobalSettings,
    is_manager?: boolean,
    stockStatus?: StockStatus
  ): Form<RelocateOptions & { stepCode: string }> {
    const options = {
      disableStocksWithSteps: init.matched,
      is_manager: is_manager,
      stockStatus: stockStatus ? stockStatus : null
    };
    return new Form<RelocateOptions & { stepCode: string }>([
      {
        name: 'relocate',
        controls: [...this.getRelocateControls(device, globalSettings, options)]
      }
    ]);
  }

  getDuplicationForm(): Form<{ nTimes: number }> {
    return new Form<{ nTimes: number }>([
      {
        name: 'duplication',
        controls: [
          {
            name: 'n_times',
            label: '',
            value: 1,
            type: 'number',
            required: true,
            unit: 'fois',
            step: '1',
            min: 1,
            max: 500
          }
        ]
      }
    ]);
  }

  getChangePasswordForm(): Form<ChangePasswordOptions> {
    return new Form<ChangePasswordOptions>([
      {
        name: 'change_password',
        controls: [
          {
            name: 'current_password',
            label: 'Mot de passe actuel',
            type: 'password',
            required: true
          },
          {
            name: 'new_password',
            label: 'Nouveau mot de passe',
            type: 'password',
            subtext: '(8 car. min, majuscule, minuscule, chiffre et caractère spécial obligatoire)',
            required: true
          },
          {
            name: 'new_password_conf',
            label: 'Nouveau mot de passe (confirmation)',
            type: 'password',
            required: true
          }
        ]
      }
    ]);
  }

  getDataExportForm(): Form<DataExportFilters> {
    return new Form<DataExportFilters>([
      {
        name: 'export',
        label: 'Export de données',
        controls: [
          {
            name: 'start_date',
            label: dicoLabelsService.getCollectionLabel('start_date'),
            type: 'datetime-local',
            value: new Date().toISOString(),
            required: true,
            split: 2
          },
          {
            name: 'end_date',
            label: dicoLabelsService.getCollectionLabel('end_date'),
            type: 'datetime-local',
            value: new Date().toISOString(),
            required: true,
            split: 2
          },
          {
            name: 'collection',
            label: 'Collection',
            type: 'dropdown',
            required: true,
            options: [
              { value: 'device_stock', label: "Stock d'ordinateurs" },
              { value: 'device_stock_adv', label: "Stock d'ordinateurs ADV" },
              { value: 'accessory_stock', label: "Stock d'accessoires" },
              { value: 'device_orders', label: "Commandes d'ordinateurs" },
              { value: 'accessory_orders', label: "Commandes d'accessoires" },
              { value: 'orders_adv', label: 'Commandes ADV' },
              { value: 'stock_movements', label: 'Mouvements de stock' },
              { value: 'controls', label: 'Contrôles' },
              { value: 'preparations', label: 'Préparations' },
              { value: 'shipments', label: 'Expéditions' },
              { value: 'process_errors', label: 'Erreurs de process' }
            ]
          }
        ]
      }
    ]);
  }
  private getPurchaseReceptionForm(purchase: Purchase): Form<Purchase> {
    return new Form<Purchase>([
      {
        name: 'reception',
        controls: [
          {
            name: 'quantity_received',
            label: dicoLabelsService.getCollectionLabel('quantity_received', 'long'),
            value: purchase.quantity_received,
            type: 'number',
            required: true,
            split: 2
          },
          {
            name: 'nb_received_packaging',
            label: dicoLabelsService.getCollectionLabel('nb_received_packaging', 'long'),
            value: purchase.nb_received_packaging,
            type: 'number',
            required: true,
            split: 2
          },
          {
            name: 'reserve_reception',
            label: dicoLabelsService.getCollectionLabel('reserve_reception'),
            type: 'toggle',
            options: [
              { value: 'true', label: 'Oui' },
              { value: 'false', label: 'Non' }
            ],
            value: purchase.reserve_reception,
            split: 2,
            afterChange: async (value: string, group) => {
              group.get('reserve_detail').hidden = Boolean(!value);
              group.get('photos').hidden = Boolean(!value);
              group.get('gallery').hidden = Boolean(!value);
            }
          },
          {
            name: 'photos',
            filename: purchase.reference_internal + '_' + Date.now(),
            type: 'photo',
            afterChange: async (value: File, group) => {
              if (!value || group.get('photos').hidden) return;
              group.get('gallery').loading = true;
              group.get('gallery').hidden = true;
              group.get('gallery').hidden = false;
              purchasesService.addPhotos(purchase.id, { photos: value }).then(purchase => {
                group.get('gallery').value = purchase.photos;
                group.get('gallery').loading = false;
                group.get('gallery').hidden = true;
                group.get('gallery').hidden = false;
              });
            }
          },
          {
            name: 'gallery',
            value: purchase.photos,
            type: 'gallery',
            afterChange: async (value: string, group) => {
              if (group.get('gallery').hidden || typeof value != 'string') return;
              group.get('gallery').loading = true;
              group.get('gallery').hidden = true;
              group.get('gallery').hidden = false;
              purchasesService.deletePhoto(purchase.id, value, 'photos').then(purchase => {
                purchasesService.get(purchase.id).then(purchase => {
                  group.get('gallery').value = purchase.photos;
                  group.get('gallery').loading = false;
                  group.get('gallery').hidden = true;
                  group.get('gallery').hidden = false;
                });
              });
            }
          },
          {
            name: 'reserve_detail',
            label: dicoLabelsService.getCollectionLabel('reserve_detail'),
            value: purchase.reserve_detail,
            type: 'text-multiline',
            required: true
          }
        ]
      }
    ]);
  }

  private getPurchaseCreationForm(purchase: Purchase, settings: GlobalSettings): Form<Purchase> {
    if (!settings) return;
    const statusIndex = ['negotiation', /*'to_pay',*/ 'shipping', 'received', 'control', 'validated'].indexOf(
      purchase.purchase_status
    );

    return new Form<Purchase>([
      {
        name: 'supplier',
        label: '<i class="fas fa-user-circle"></i> Fournisseur',
        controls: [
          {
            name: 'supplier_id',
            label: dicoLabelsService.getCollectionLabel('last_name', 'short'),
            type: 'dropdown',
            options: [],
            value: purchase.supplier?.id,
            disabled: statusIndex > 0,
            required: true,
            afterChange: (value: string, group) => {
              const options = group.get('supplier_id').options;
              if (!options?.length) {
                // Options init
                return suppliersService.order_list({ filters: { category: 'device' } }).then(resp => {
                  const suppliers = resp.values || [];

                  if (!suppliers) throw new Error('Impossible de récupérer la liste des fournisseurs');
                  group.get('supplier_id').options = suppliers?.map(value => ({
                    value: value.id,
                    label: value.display_name,
                    details: value
                  }));
                });
              } else {
                const option = options.find(opt => opt.value === value);
                group.get('supplier_country').value = option?.details?.country;
                group.get('currency').value = option?.details?.currency;
                group.get('tax_scheme').value = option?.details?.tax_scheme;
                group.get('payment_timing').value = option?.details?.payment_timing;
                group.get('payment_delay').value = option?.details?.payment_delay;
                group.get('payment_delay').hidden = option?.details?.payment_timing !== 'delay';
              }
            }
          },
          {
            name: 'item_type',
            type: 'text',
            value: 'device',
            hidden: true
          },
          {
            name: 'supplier_country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'text',
            value: purchase.supplier?.country,
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            value: purchase.currency,
            disabled: false,
            options: [
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'usd', label: 'USD' }
            ],
            required: true,
            split: 3
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'short'),
            value: purchase.tax_scheme,
            type: 'dropdown',
            options: settings.schemes.map((scheme, index) => ({
              value: scheme[0],
              label: scheme[1],
              indexWeight: index
            })),
            disabled: false,
            required: true,
            split: 3
          },
          {
            name: 'incoterm',
            label: dicoLabelsService.getCollectionLabel('incoterm', 'short'),
            value: purchase.incoterm,
            type: 'dropdown',
            options: ['CIF', 'CIP', 'CFR', 'CPT', 'DAP', 'DDP', 'DPU', 'EXW', 'FAS', 'FCA', 'FOB'].map(
              (item, index) => ({
                value: item,
                label: item,
                indexWeight: index
              })
            ),
            disabled: false,
            required: false,
            split: 2
          },
          {
            name: 'reference_supplier',
            label: dicoLabelsService.getCollectionLabel('reference_supplier', 'long'),
            type: 'text',
            value: purchase.reference_supplier,
            disabled: false,
            required: false,
            split: 2
          },
          ...(statusIndex === 0
            ? ([
                {
                  name: 'payment_timing',
                  label: dicoLabelsService.getCollectionLabel('payment_timing', 'long'),
                  type: 'dropdown',
                  value: purchase.payment_timing || purchase.supplier?.payment_timing,
                  options: [
                    { value: 'before_reception', label: 'Avant expédition' },
                    { value: 'at_reception', label: 'A la livraison' },
                    { value: 'delay', label: 'A X jours' }
                  ],
                  required: true,
                  split: 2,
                  afterChange: async (value: string, group) => {
                    group.get('payment_delay').hidden = value !== 'delay';
                    group.get('purchase_types').split = value !== 'delay' ? 2 : 1;
                  }
                },
                {
                  name: 'payment_delay',
                  label: dicoLabelsService.getCollectionLabel('payment_delay', 'long'),
                  type: 'number',
                  value: purchase.payment_delay || purchase.supplier?.payment_delay,
                  required: true,
                  split: 2
                }
              ] as FormControlRaw<Purchase>[])
            : []),
          {
            name: 'purchase_types',
            label: dicoLabelsService.getCollectionLabel('purchase_types'),
            type: 'dropdown',
            value: purchase.purchase_types,
            options: settings.purchase_types.map(type => ({
              value: type[0],
              label: type[1]
            })),
            required: true,
            split: 2
          },
          {
            name: 'supplier_order',
            label: dicoLabelsService.getCollectionLabel('supplier_order'),
            type: 'text',
            value: purchase.supplier_order,
            required: false
          }
        ]
      }
    ]);
  }
  private getSupplierCreationForm(supplier: Supplier, globalSettings: GlobalSettings): Form<Supplier> {
    if (!globalSettings) return;

    return new Form<Supplier>([
      {
        name: 'supplier',
        controls: [
          {
            name: 'display_name',
            label: dicoLabelsService.getCollectionLabel('display_name', 'long'),
            type: 'text',
            value: supplier.display_name,
            required: true,
            split: 1
          },
          {
            name: 'category',
            label: dicoLabelsService.getCollectionLabel('category', 'long'),
            type: 'dropdown',
            options: [
              { value: 'device', label: 'Ordinateurs' },
              { value: 'spare', label: 'Pièces détachées' },
              { value: 'consumable', label: 'Emballages' },
              { value: 'other', label: 'Autre' }
            ],
            value: supplier.category,
            required: true,
            split: 1
          },
          {
            name: 'country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            value: supplier.country,
            options: globalSettings.countries.map((scheme, index) => ({
              value: scheme[0],
              label: scheme[1],
              indexWeight: index
            })),
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            value: supplier.currency,
            options: [
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'usd', label: 'USD' }
            ],
            required: true,
            split: 3
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'long'),
            value: supplier.tax_scheme,
            type: 'dropdown',
            options: globalSettings.schemes.map((scheme, index) => ({
              value: scheme[0],
              label: scheme[1],
              indexWeight: index
            })),
            required: true,
            split: 3
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            value: supplier.warranty,
            unit: dicoLabelsService.getUnit('day'),
            required: true,
            split: 2
          },
          {
            name: 'warranty_types',
            label: dicoLabelsService.getCollectionLabel('warranty_types'),
            type: 'dropdown',
            value: supplier.warranty_types,
            options: globalSettings.warranty_types.map(rma_type => ({
              value: rma_type[0],
              label: rma_type[1]
            })),
            required: true,
            split: 2
          },
          {
            name: 'payment_timing',
            label: dicoLabelsService.getCollectionLabel('payment_timing', 'long'),
            type: 'dropdown',
            value: supplier.payment_timing,
            options: [
              { value: 'before_reception', label: 'Avant la livraison' },
              { value: 'at_reception', label: 'A la livraison' },
              { value: 'delay', label: 'A X jours' }
            ],
            required: true,
            split: 2
          },
          {
            name: 'comment',
            label: dicoLabelsService.getCollectionLabel('comment', 'long'),
            type: 'text-multiline',
            value: supplier.comment,
            split: 1
          }
        ]
      }
    ]);
  }
  getPurchaseDealForm(purchase: Purchase): Form<PurchaseDealOptions> {
    return new Form<PurchaseDealOptions>([
      {
        name: 'deal',
        controls: [
          {
            name: 'analysis_supplier_file',
            label: dicoLabelsService.getCollectionLabel('analysis_supplier_file', 'long'),
            type: 'file',
            value: purchase.analysis_supplier_file
          }
        ]
      }
    ]);
  }

  getPurchasePauseForm(): Form<PurchasePauseOptions> {
    return new Form<PurchasePauseOptions>([
      {
        name: 'pause-options',
        controls: [
          {
            name: 'comment',
            label: dicoLabelsService.getCollectionLabel('comment', 'long'),
            type: 'text-multiline',
            required: true
          }
        ]
      }
    ]);
  }

  getSupplierRibForm(rib: SupplierRib, globalSettings: GlobalSettings): Form<SupplierRib> {
    return new Form<SupplierRib>([
      {
        name: 'rib-options',
        controls: [
          {
            name: 'iban',
            label: dicoLabelsService.getCollectionLabel('iban', 'long'),
            type: 'text',
            value: rib?.iban,
            required: true
          },
          {
            name: 'bic',
            label: dicoLabelsService.getCollectionLabel('bic', 'long'),
            type: 'text',
            value: rib?.bic,
            split: 2,
            required: true
          },
          {
            name: 'country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map(country => ({
              value: country[0],
              label: country[1]
            })),
            value: rib?.country,
            required: true,
            split: 4
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            options: [
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'usd', label: 'USD' }
            ],
            value: rib?.currency,
            required: true,
            split: 4
          }
        ]
      }
    ]);
  }

  getOrdersImportForm(): Form<OrdersImportOptions> {
    return new Form<OrdersImportOptions>([
      {
        // name: 'orders',
        name: 'orderLines',
        controls: [
          {
            name: 'marketplace',
            label: dicoLabelsService.getCollectionLabel('marketplace', 'long'),
            type: 'dropdown',
            options: [
              { value: 'XXX', label: "N'importe" },
              { value: 'GRE', label: 'Greenweez' },
              { value: 'PHH', label: 'Phone House' },
              { value: 'PGB', label: 'Pigsback' },
              { value: 'QBP', label: 'Quel Bon Plan' }
            ],
            required: true
          },
          {
            name: 'orders_file',
            label: dicoLabelsService.getCollectionLabel('orders_file', 'long'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  getBatchStockWithdrawalForm(): Form<LocationBatchWithdrawalOptions> {
    return new Form<LocationBatchWithdrawalOptions>([
      {
        name: 'locations',
        controls: [
          {
            name: 'batch_stock_withdrawal_file',
            label: dicoLabelsService.getCollectionLabel('location_batch_withdrawal', 'long'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  getMarketplaceSkusImportForm(): Form<MarketplaceSkuImportOptions> {
    return new Form<MarketplaceSkuImportOptions>([
      {
        name: 'marketplace-skus',
        controls: [
          {
            name: 'marketplace_skus_file',
            label: dicoLabelsService.getCollectionLabel('marketplace_skus_file', 'long'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  getMarketplaceSkusPriceImportForm(): Form<MarketplaceSkuImportOptions> {
    return new Form<MarketplaceSkuImportOptions>([
      {
        name: 'marketplace-skus',
        controls: [
          {
            name: 'marketplace_skus_price_file',
            label: dicoLabelsService.getCollectionLabel('marketplace_skus_price_file', 'long'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  getSkuDifferenceCostImportForm(): Form<SkuDifferenceCostImportOptions> {
    return new Form<SkuDifferenceCostImportOptions>([
      {
        name: 'sku-difference-cost',
        controls: [
          {
            name: 'sku_difference_cost_file',
            label: dicoLabelsService.getCollectionLabel('sku-difference-cost'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  getRepairCostImportForm(): Form<RepairCostImportOptions> {
    return new Form<RepairCostImportOptions>([
      {
        name: 'repair-cost',
        controls: [
          {
            name: 'repair_cost_file',
            label: dicoLabelsService.getCollectionLabel('repair-cost'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  getImportUpdateStatusForm(): Form<ImportUpdateStatusOptions> {
    return new Form<ImportUpdateStatusOptions>([
      {
        name: 'label-update-status',
        controls: [
          {
            name: 'label_update_status_file',
            label: dicoLabelsService.getCollectionLabel('label-update-status'),
            type: 'file',
            required: true
          }
        ]
      }
    ]);
  }

  private getRelocateControls(
    device: Device,
    globalSettings: GlobalSettings,
    options: {
      /**
       * We can prevent the user from changing stock value
       */
      disableStockDropdown?: boolean;
      /**
       * We can disable the stocks with steps (Preparation and Expedition, because they require the device or order to be matched)
       */
      disableStocksWithSteps?: boolean;
      /**
       * Allows to filter options on a list of stocks (e.g. in Expedition, for an iMac device, the user should be
       * allowed to send the device to Preparation or Expedition)
       */
      stocksSubSet?: [number, StockStatus][];

      is_manager?: boolean;
      stockStatus?: StockStatus;
    }
  ): FormControlRaw<{ stockId: number; stepCode: string }>[] {
    const { disableStockDropdown, disableStocksWithSteps, stocksSubSet, is_manager } = options;
    if (!globalSettings) return null;
    const stocks_status = stocksSubSet ? stocksSubSet : globalSettings.stocks_status;
    let initStock;
    if (options.stockStatus) {
      initStock = options.stockStatus;
    } else {
      const found = device && stocks_status.find(s => +s[0] === device.stock_status_id);
      initStock = found ? found[1] : null;
    }
    const stocks = stocksStatusService.list_stock(stocks_status);
    return [
      {
        name: 'stockId',
        label: dicoLabelsService.getCollectionLabel('stock', 'long'),
        type: 'dropdown',
        options: stocks.map(stock => {
          const data = {
            value: stock[0],
            label: stock[1].display_name
          };
          return data;
        }),
        required: true,
        disabled: disableStockDropdown,
        value: initStock?.stock_id,
        afterChange: async (value: number, group) => {
          const stock_status = stocks_status.find(s => s[1].stock.id === value);

          const statusFields = group.get('statusId');
          statusFields.value = null;
          const filter_status = stocks_status.filter(s => s[1].stock.id == value);
          if (filter_status[0]) {
            statusFields.options = filter_status
              .map(stock_status => {
                if (stock_status[1].status && (!stock_status[1].no_relocate || is_manager)) {
                  return {
                    value: stock_status[1].status.id,
                    label: stock_status[1].status.display_name
                  };
                }
              })
              .filter(s => s);

            statusFields.hidden = !statusFields.options[0];

            if (
              (stock_status[1].no_relocate && disableStocksWithSteps) ||
              !stocks_status.find(s => s[1].stock_id == stock_status[1].stock_id && !s[1].status_id)
            ) {
              statusFields.required = true;
              statusFields.label = dicoLabelsService.getCollectionLabel('status_required');
            } else {
              statusFields.required = false;
              statusFields.label = dicoLabelsService.getCollectionLabel('status', 'long');
            }
            statusFields.value = initStock?.status_id;
          }
        },
        split: 2
      },
      {
        name: 'statusId',
        label: dicoLabelsService.getCollectionLabel('status', 'long'),
        type: 'dropdown',
        hidden: true,
        required: false,
        split: 2
      }
    ];
  }

  private getDeviceForm(device: Device, type: DeviceFormType, globalSettings: GlobalSettings): Form<Device> {
    const test = [];
    const hasBattery = !/(i\s?mac)|(mac\s?mini)|(mac\s?pro)/i.test(device.model_namestring);

    test.push({
      name: 'message',
      type: 'message-insert',
      content: '',
      theme: '',
      hidden: true
    });
    if (type == 'control' && device.id) {
      test[0].content =
        'Cet ordinateur a déjà été contrôlé le &nbsp;' +
        '<span class="emphasis">' +
        datesService.format(get(device, 'created_at')) +
        '</span>' +
        '</div>';
      test[0].theme = 'warning';
      test[0].hidden = false;
    }
    let supplierComment = '';
    if (device.purchase_items?.supplier_grade) supplierComment += device.purchase_items.supplier_grade;
    if (device.purchase_items?.supplier_grade && device.purchase_items.supplier_comment) supplierComment += '\n';
    if (device.purchase_items?.supplier_comment) supplierComment += device.purchase_items.supplier_comment;
    let hiddenSupplierComment = true;
    if (type == 'device') hiddenSupplierComment = false;

    const rebuildSku = form => this.buildSkuOkamac(form.value, globalSettings, device.grade);

    const updateSku = (_value, group) => {
      const form = group.parent;
      // Keep the keyboard/cellular field in sync with the live selected model,
      // since it's built once and the model can change afterward (e.g. in the control form).
      const isIpad = (form.get('model.model_namestring').value as string)?.split(' ')[0] === 'iPad';
      // The control's name is toggled between 'keyboard'/'cellular' below, so look it up by whichever it currently is.
      const keyboardControl = form.has('model.keyboard') ? form.get('model.keyboard') : form.get('model.cellular');
      const keyboardLabel = isIpad
        ? dicoLabelsService.getCollectionLabel('cellular', 'long')
        : dicoLabelsService.getCollectionLabel('keyboard', 'long');
      // The control's name must match the backend attribute it submits to (control.cellular vs control.keyboard).
      keyboardControl.name = isIpad ? 'cellular' : 'keyboard';
      keyboardControl.label = keyboardLabel;
      // placeholder isn't reactive (unlike label/options), so it must be recomputed manually here
      keyboardControl.placeholder = keyboardLabel + '...';
      keyboardControl.options = (isIpad ? globalSettings.cellular : globalSettings.keyboards).map(
        (keyboard, index) => ({
          value: keyboard[0],
          label: keyboard[1],
          indexWeight: index
        })
      );
      // graphic_card/ram required flag is set once at form build time from the initial model;
      // keep it in sync when the model changes afterward (e.g. manual device entry / control form).
      if (form.has('perfs.graphic_card')) form.get('perfs.graphic_card').required = !isIpad;
      if (form.has('perfs.ram')) form.get('perfs.ram').required = !isIpad;
      // In control context (Control2Form), always use the rebuilt SKU.
      // In device context (DeviceInfosModal), device.sku > device.supplier_sku > rebuilt SKU.
      if (type === 'control') {
        form.get('model.sku_okamac').value = rebuildSku(form);
      } else {
        form.get('model.sku_okamac').value = device.sku || device.supplier_sku || rebuildSku(form);
      }
    };

    const form = [
      {
        name: 'message',
        controls: test
      },
      {
        name: 'model',
        label: '<i class="fas fa-laptop"></i> Modèle',
        controls: this.getDeviceModelControls(device, type, globalSettings, updateSku)
      },
      {
        name: 'perfs',
        label: '<i class="fas fa-chart-line"></i> Performances',
        controls: this.getDevicePerfsControls(device, globalSettings, updateSku)
      }
    ];
    if (hasBattery)
      form.push({
        name: 'battery',
        label: '<i class="fas fa-car-battery"></i> Batterie',
        controls: this.getDeviceBatteryControls(device)
      });

    return new Form<Device>([
      ...form,
      ...[
        {
          name: 'supplier',
          label: '<i class="far fa-handshake"></i> Informations fournisseur',
          controls: this.getDeviceSupplierControls(device, type, globalSettings)
        },
        {
          name: 'supplier_comment',
          controls: [
            {
              name: 'supplier_comment',
              label: dicoLabelsService.getCollectionLabel('supplier_comment'),
              type: 'text-multiline',
              value: supplierComment,
              disabled: true,
              hidden: hiddenSupplierComment,
              split: 1
            }
          ]
        },
        {
          name: 'grading',
          label: '<i class="fas fa-chart-line"></i> Grading',
          controls: this.getDeviceGradingsControls(device, type)
        },
        {
          name: 'comment',
          controls: [
            {
              name: 'initial_comment',
              label: dicoLabelsService.getCollectionLabel('initial_comment', 'long'),
              type: 'text-multiline',
              value: device.initial_comment
            }
          ]
        }
      ]
    ]);
  }

  private getControlRelocateForm(): Form<Device> {
    return new Form<Device>([
      {
        name: 'comment',
        controls: [
          {
            name: 'id_temp',
            label: dicoLabelsService.getCollectionLabel('idTemp'),
            type: 'text',
            required: false,
            preventEnterSubmit: true
          },
          {
            name: 'comment',
            label: dicoLabelsService.getCollectionLabel('comment', 'long'),
            type: 'text-multiline'
          }
        ]
      }
    ]);
  }

  private getCageCreationForm(cage: Cage, globalSettings: GlobalSettings): Form<Cage> {
    return new Form<Cage>([
      {
        name: 'information',
        label: '<i class="fas fa-info-circle"></i> Informations',
        controls: [
          {
            name: 'carrier',
            label: dicoLabelsService.getCollectionLabel('carrier'),
            type: 'option-list',
            options: globalSettings.shipping_companies
              .filter(sc => ['DHL', 'Colissimo', 'Palette', 'Chronopost', 'UPS'].includes(sc[1].display_name))
              .map(sc => ({
                value: sc[0],
                label: sc[1].display_name
              })),
            value: cage.name,
            required: true
          },
          {
            name: 'cage_number',
            label: dicoLabelsService.getCollectionLabel('cage_number'),
            type: 'option-list',
            options: ['1', '2', '3', '4', '5', '6'].map(num => ({
              value: num,
              label: num
            })),
            value: cage.cage_number,
            required: true
          }
        ]
      }
    ]);
  }

  private getOrderLineCreationForm(
    orderLine: OrderLine,
    globalSettings: GlobalSettings
  ): Form<OrderLine & OrderAddress> {
    return new Form<OrderLine & OrderAddress>([
      {
        name: 'purchase',
        label: '<i class="fas fa-shopping-basket"></i> Achat',
        controls: [
          {
            name: 'order_line_type',
            label: dicoLabelsService.getCollectionLabel('order_line_type'),
            type: 'dropdown',
            options: globalSettings.order_line_types.map((type, index) => ({
              value: type[0],
              label: type[1],
              indexWeight: index
            })),
            value: orderLine.order_line_type || globalSettings.order_line_types[0][0],
            required: true,
            afterChange: async (value: string, group) => {
              // Adapt form depending on if the order is for a device or an accessory/part
              const isDeviceOrderLine = value === 'device';
              group.get('sku_marketplace').hidden = !isDeviceOrderLine;
              group.get('sku_okamac').label = isDeviceOrderLine
                ? dicoLabelsService.getCollectionLabel('sku_okamac', 'long')
                : dicoLabelsService.getCollectionLabel('accessory_part_id');
              // group.get('sku_okamac').split = isDeviceOrderLine ? 2 : 1;
              group.get('sku_okamac').required = isDeviceOrderLine ? false : true;
            },
            split: 3
          },
          {
            name: 'price',
            label: dicoLabelsService.getCollectionLabel('price', 'long'),
            type: 'number',
            // value: orderLine.price,
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            options: globalSettings.currencies.map((currency, index) => ({
              value: currency[0],
              label: `${currency[1]} (${currency[0]})`,
              indexWeight: index
            })),
            // value: orderLine.order.currency,
            value: globalSettings.currencies[0][0],
            required: true,
            split: 3
          },
          {
            name: 'product_description',
            label: dicoLabelsService.getCollectionLabel('product_description', 'long'),
            type: 'text'
            // value: orderLine.product_description
          },
          {
            name: 'sku_marketplace',
            label: dicoLabelsService.getCollectionLabel('sku_marketplace', 'long'),
            type: 'text',
            hidden: orderLine.order_line_type ? orderLine.order_line_type !== 'device' : false
            // value: orderLine.sku_marketplace,
          },
          {
            name: 'sku_okamac',
            label:
              orderLine.order_line_type && orderLine.order_line_type !== 'device'
                ? dicoLabelsService.getCollectionLabel('accessory_part_id')
                : dicoLabelsService.getCollectionLabel('sku_okamac', 'long'),
            type: 'text',
            required: orderLine.order_line_type ? orderLine.order_line_type !== 'device' : false,
            split: 1
            // value: orderLine.sku_okamac,
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            // value: orderLine.warranty,
            unit: dicoLabelsService.getUnit('month'),
            required: true,
            split: 2
          },
          {
            name: 'warranty_amount',
            label: dicoLabelsService.getCollectionLabel('warranty_amount', 'long'),
            type: 'number',
            value: orderLine.warranty_amount,
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'short'),
            value: orderLine.tax_scheme,
            type: 'dropdown',
            options: [
              ...globalSettings.schemes.map(scheme => ({
                value: scheme[0],
                label: scheme[1]
              })),
              ...[{ value: null, label: 'Aucun' }]
            ],
            split: 2
          }
        ]
      },
      {
        name: 'battery',
        label: '<i class="fas fa-battery-full"></i> Batterie neuve',
        controls: [
          {
            name: 'new_battery_required',
            label: dicoLabelsService.getCollectionLabel('new_battery_required', 'long'),
            type: 'toggle',
            value: get(orderLine, 'new_battery_required'),
            split: 2,
            afterChange: async (value: boolean, group) => {
              group.get('new_battery_price').hidden = value !== true;
              if (value !== true) {
                group.get('new_battery_price').value = null;
              }
            }
          },
          {
            name: 'new_battery_price',
            label: dicoLabelsService.getCollectionLabel('new_battery_price', 'long'),
            type: 'number',
            value: get(orderLine, 'new_battery_price'),
            unit: dicoLabelsService.getUnit('money'),
            required: true,
            split: 2
          }
        ]
      }
    ]);
  }

  private getOrderLineForm(
    orderLine: OrderLine,
    globalSettings: GlobalSettings,
    options?
  ): Form<OrderLine & OrderAddress> {
    const shippingCompany = globalSettings.shipping_companies.find(sp => sp[0] === orderLine.shipping_company);
    const shippingTypes = (shippingCompany && shippingCompany[1].types) || [];
    const status_code = globalSettings?.expedition_status?.find(sp => sp[0] === String(orderLine.carrier_label_status));
    const statusLabel = status_code ? status_code[1] : '';
    const isDeviceOrderLine = orderLine.order_line_type === 'device';
    return new Form<OrderLine & OrderAddress>([
      {
        name: 'purchase',
        label: '<i class="fas fa-shopping-basket"></i> Achat',
        controls: [
          {
            name: 'marketplace',
            label: dicoLabelsService.getCollectionLabel('marketplace', 'short'),
            type: 'text',
            value: orderLine.order.marketplace.display_name,
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'marketplace_order_id',
            label: dicoLabelsService.getCollectionLabel('marketplace_order_id', 'long'),
            type: 'text',
            value: orderLine.order.marketplace_order_id,
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'order_date',
            label: dicoLabelsService.getCollectionLabel('order_date', 'long'),
            type: 'datetime-local',
            value: orderLine.order.order_date,
            required: true,
            split: 3
          },
          {
            name: 'price',
            label: dicoLabelsService.getCollectionLabel('price', 'long'),
            type: 'number',
            value: orderLine.price,
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            options: globalSettings.currencies.map((currency, index) => ({
              value: currency[0],
              label: `${currency[1]} (${currency[0]})`,
              indexWeight: index
            })),
            value: orderLine.order.currency,
            required: true,
            split: 4
          },
          {
            name: 'product_description',
            label: dicoLabelsService.getCollectionLabel('product_description', 'long'),
            type: 'text',
            value: orderLine.product_description
          },
          {
            name: 'sku_marketplace',
            label: dicoLabelsService.getCollectionLabel('sku_marketplace', 'long'),
            type: 'text',
            value: orderLine.sku_marketplace,
            required: true,
            split: 2,
            hidden: !isDeviceOrderLine
          },
          {
            name: 'sku_okamac',
            label: isDeviceOrderLine
              ? dicoLabelsService.getCollectionLabel('sku_okamac', 'long')
              : dicoLabelsService.getCollectionLabel('accessory_part_id'),
            type: 'text',
            disabled: false,
            value: orderLine.sku_okamac,
            split: isDeviceOrderLine ? 2 : 1,
            required: isDeviceOrderLine ? false : true
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            value: orderLine.warranty,
            unit: dicoLabelsService.getUnit('month'),
            required: true,
            split: 2
          },
          {
            name: 'warranty_amount',
            label: dicoLabelsService.getCollectionLabel('warranty_amount', 'long'),
            type: 'number',
            value: orderLine.warranty_amount,
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'payment_method',
            label: dicoLabelsService.getCollectionLabel('payment_method'),
            type: 'dropdown',
            options: globalSettings.payment_methods.map((payment_method, index) => ({
              value: payment_method[0],
              label: payment_method[1],
              indexWeight: index
            })),
            value: orderLine.order.payment_method,
            split: 2
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'short'),
            value: orderLine.tax_scheme,
            type: 'dropdown',
            options: [
              ...globalSettings.schemes.map(scheme => ({
                value: scheme[0],
                label: scheme[1]
              })),
              ...[{ value: null, label: 'Aucun' }]
            ],
            split: 2
          },
          {
            name: 'os_code_to_install',
            label: dicoLabelsService.getCollectionLabel('ordered_os'),
            value: orderLine.os_code_to_install,
            type: 'dropdown',
            options: [
              ...globalSettings.operating_systems.map(scheme => ({
                value: scheme[0],
                label: scheme[1].display_name
              })),
              ...[{ value: null, label: 'Aucun' }]
            ]
          }
        ]
      },
      {
        name: 'dispatch_info',
        label: '<i class="fas fa-dolly"></i> Expédition',
        controls: [
          {
            name: 'express',
            label: dicoLabelsService.getCollectionLabel('express', 'long'),
            type: 'toggle',
            value: orderLine.order.express
          },
          {
            name: 'shipping_company',
            label: dicoLabelsService.getCollectionLabel('shipping_company', 'long'),
            type: 'dropdown',
            options: globalSettings.shipping_companies.map((sc, index) => ({
              value: sc[0],
              label: sc[1].display_name,
              indexWeight: index
            })),
            value: orderLine.shipping_company,
            afterChange: async (value: string, group) => {
              const company = globalSettings.shipping_companies.find(sp => sp[0] === value);
              const types = (company && company[1].types) || [];
              const options = types.map(type => ({
                value: type,
                label: type
              }));
              group.get('shipping_type').options = options;
            },
            required: true,
            split: 2
          },
          {
            name: 'shipping_type',
            label: dicoLabelsService.getCollectionLabel('shipping_type', 'long'),
            type: 'dropdown',
            options: shippingTypes.map(type => ({
              value: type,
              label: type
            })),
            required: true,
            value: orderLine.shipping_type,
            split: 2
          },
          {
            name: 'max_shipping_date',
            label: dicoLabelsService.getCollectionLabel('max_shipping_date', 'long'),
            type: 'datetime-local',
            value: orderLine.max_shipping_date,
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'shipping_date',
            label: dicoLabelsService.getCollectionLabel('shipping_date', 'long'),
            type: 'datetime-local',
            value: orderLine.shipping_date,
            disabled: !options?.actionUpdateOrderShippingDate,
            split: 2
          },
          {
            name: 'shipping_tracking_number',
            label: dicoLabelsService.getCollectionLabel('shipping_tracking_number', 'long'),
            type: 'text',
            value: orderLine.shipping_tracking_number,
            disabled: true,
            split: 2
          },
          {
            name: 'expedition_status',
            label: dicoLabelsService.getCollectionLabel('expedition_status'),
            type: 'text',
            value: statusLabel,
            required: false,
            split: 2
          },
          {
            name: 'manual_tracking_number',
            label: dicoLabelsService.getCollectionLabel('manual_tracking_number'),
            type: 'text',
            value: orderLine.manual_tracking_number,
            required: false,
            split: 2
          }
        ]
      },
      {
        name: 'shipping_info',
        label: '<i class="fas fa-dolly"></i> Adresse de livraison',
        controls: [
          {
            name: 'shipping_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.last_name'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.first_name'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.company')
          },
          {
            name: 'shipping_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.email'),
            split: 3
          },
          {
            name: 'shipping_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(orderLine.order, 'shipping_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            split: 4
          },
          {
            name: 'shipping_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.phone'),
            split: 3
          },
          {
            name: 'shipping_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.street_1'),
            required: true
          },
          {
            name: 'shipping_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(orderLine.order, 'shipping_info.street_2')
          },
          {
            name: 'shipping_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.postal_code'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(orderLine.order, 'shipping_info.city'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(orderLine.order, 'shipping_info.country'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.relay_id',
            label: 'nothing',
            type: 'text',
            value: get(orderLine.order, 'shipping_info.relay_id'),
            split: 3,
            hidden: true
          }
        ]
      },
      {
        name: 'billing_info',
        label: '<i class="fas fa-money-bill-alt"></i> Adresse de facturation',
        controls: [
          {
            name: 'same_address',
            label: dicoLabelsService.getCollectionLabel('same_address', 'long'),
            type: 'toggle',
            value: get(orderLine.order, 'same_address'),
            afterChange: async (value: boolean, group) => {
              group.get('billing_info.last_name').hidden = value;
              group.get('billing_info.first_name').hidden = value;
              group.get('billing_info.company').hidden = value;
              group.get('billing_info.vat_number').hidden = value;
              group.get('billing_info.nif_code').hidden = value;
              group.get('billing_info.email').hidden = value;
              group.get('billing_info.phone').hidden = value;
              group.get('billing_info.street_1').hidden = value;
              group.get('billing_info.street_2').hidden = value;
              group.get('billing_info.postal_code').hidden = value;
              group.get('billing_info.city').hidden = value;
              group.get('billing_info.country').hidden = value;
              group.get('billing_info.dial_code').hidden = value;
            }
          },
          {
            name: 'billing_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.last_name'),
            required: true,
            hidden: get(orderLine.order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.first_name'),
            required: true,
            hidden: get(orderLine.order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.company'),
            hidden: get(orderLine.order, 'same_address')
          },
          {
            name: 'billing_info.vat_number',
            label: dicoLabelsService.getCollectionLabel('vat_number', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.vat_number'),
            hidden: get(orderLine.order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.nif_code',
            label: dicoLabelsService.getCollectionLabel('nif_code', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.nif_code'),
            split: 2
          },
          {
            name: 'billing_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.email'),
            hidden: get(orderLine.order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(orderLine.order, 'billing_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            hidden: get(orderLine.order, 'same_address'),
            split: 4
          },
          {
            name: 'billing_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.phone'),
            hidden: get(orderLine.order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.street_1'),
            required: true,
            hidden: get(orderLine.order, 'same_address')
          },
          {
            name: 'billing_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(orderLine.order, 'billing_info.street_2'),
            hidden: get(orderLine.order, 'same_address')
          },
          {
            name: 'billing_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.postal_code'),
            required: true,
            hidden: get(orderLine.order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(orderLine.order, 'billing_info.city'),
            required: true,
            hidden: get(orderLine.order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(orderLine.order, 'billing_info.country'),
            required: true,
            hidden: get(orderLine.order, 'same_address'),
            split: 3
          }
        ]
      },
      {
        name: 'battery',
        label: '<i class="fas fa-battery-full"></i> Batterie neuve',
        controls: [
          {
            name: 'new_battery_required',
            label: dicoLabelsService.getCollectionLabel('new_battery_required', 'long'),
            type: 'toggle',
            value: get(orderLine, 'new_battery_required'),
            split: 2,
            afterChange: async (value: boolean, group) => {
              group.get('new_battery_price').hidden = value !== true;
              if (value !== true) {
                group.get('new_battery_price').value = null;
              }
            }
          },
          {
            name: 'new_battery_price',
            label: dicoLabelsService.getCollectionLabel('new_battery_price', 'long'),
            type: 'number',
            value: get(orderLine, 'new_battery_price'),
            unit: dicoLabelsService.getUnit('money'),
            required: true,
            split: 2
          }
        ]
      },
      {
        name: 'status',
        label: '<i class="fas fa-headset"></i> Demandes particulières',
        controls: [
          {
            name: 'new_match_required',
            label: dicoLabelsService.getCollectionLabel('new_match_required', 'long'),
            type: 'toggle',
            value: get(orderLine, 'new_match_required'),
            split: 2
          },
          {
            name: 'exchanged',
            label: dicoLabelsService.getCollectionLabel('exchanged', 'long'),
            type: 'toggle',
            disabled: true,
            value: get(orderLine, 'exchanged'),
            split: 2
          },
          {
            name: 'out_of_stock',
            label: dicoLabelsService.getCollectionLabel('out_of_stock', 'long'),
            type: 'toggle',
            value: get(orderLine, 'out_of_stock'),
            split: 2
          },
          {
            name: 'ordered_keyboard',
            label: dicoLabelsService.getCollectionLabel('ordered_keyboard', 'long'),
            type: 'dropdown',
            value: get(orderLine, 'ordered_keyboard'),
            options: globalSettings.accessories
              .filter(acc => acc[1].product_type === 'keyboard')
              .map(acc => ({
                value: acc[1].code,
                label: acc[1].desc
              }))
              .concat([{ value: 'none', label: 'Aucun' }]),
            hidden: !(
              orderLine.order_line_type === 'device' &&
              (orderLine.ordered_item?.model_namestring === 'iMac' ||
                orderLine.ordered_item?.model_namestring === 'Mac Mini' ||
                orderLine.ordered_item?.model_namestring === 'Mac Pro' ||
                orderLine.ordered_item?.model_namestring === 'Mac Studio')
            )
          },
          {
            name: 'ordered_mouse',
            label: dicoLabelsService.getCollectionLabel('ordered_mouse', 'long'),
            type: 'dropdown',
            value: get(orderLine, 'ordered_mouse'),
            options: globalSettings.accessories
              .filter(acc => acc[1].product_type === 'mouse')
              .map(acc => ({
                value: acc[1].code,
                label: acc[1].desc
              }))
              .concat([{ value: 'none', label: 'Aucun' }]),
            hidden: !(
              orderLine.order_line_type === 'device' &&
              (orderLine.ordered_item?.model_namestring === 'iMac' ||
                orderLine.ordered_item?.model_namestring === 'Mac Mini' ||
                orderLine.ordered_item?.model_namestring === 'Mac Pro' ||
                orderLine.ordered_item?.model_namestring === 'Mac Studio')
            )
          }
        ]
      }
    ]);
  }

  private getMarketplaceForm(marketplace: Marketplace, globalSettings: GlobalSettings, options?): Form<Marketplace> {
    return new Form<Marketplace>([
      {
        name: 'settings',
        label: '<i class="fas fa-cog"></i> Paramètres',
        controls: [
          {
            name: 'display_name',
            label: dicoLabelsService.getCollectionLabel('display_name', 'short'),
            type: 'text',
            value: marketplace.display_name,
            required: true,
            split: 2
          },
          {
            name: 'code',
            label: dicoLabelsService.getCollectionLabel('code', 'short'),
            type: 'text',
            value: marketplace.code,
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'sav_dispatch_time',
            label: dicoLabelsService.getCollectionLabel('sav_dispatch_time', 'short'),
            type: 'number',
            unit: dicoLabelsService.getUnit('day'),
            value: marketplace.sav_dispatch_time,
            required: true,
            split: 2
          },
          {
            name: 'mirakl',
            label: dicoLabelsService.getCollectionLabel('is_mirakl', 'short'),
            type: 'toggle',
            value: marketplace.mirakl,
            required: true,
            afterChange: async (value: boolean, group) => {
              group.get('from_shippy_pro').hidden = value;
            },
            split: 2
          },
          {
            name: 'mode',
            label: dicoLabelsService.getCollectionLabel('selling_strat', 'short'),
            type: 'dropdown',
            options: [
              { value: 'margin', label: 'Marge' },
              { value: 'flowing', label: 'Écoulement' }
            ],
            value: marketplace.mode,
            required: true,
            split: 1
          },
          {
            name: 'from_shippy_pro',
            label: dicoLabelsService.getCollectionLabel('from_shippy', 'short'),
            type: 'toggle',
            value: marketplace.from_shippy_pro,
            required: true,
            hidden: get(marketplace, 'mirakl'),
            afterChange: async (value: boolean, group) => {
              group.get('shippy_pro_name').hidden = !value;
            },
            split: 2
          },
          {
            name: 'shippy_pro_name',
            label: dicoLabelsService.getCollectionLabel('shippy_name', 'short'),
            type: 'text',
            value: marketplace.shippy_pro_name,
            required: true,
            hidden: !get(marketplace, 'from_shippy_pro'),
            split: 2
          }
        ]
      },
      {
        name: 'alias',
        label: '<i class="fas fa-clone"></i> Alias',
        controls: [
          {
            name: 'alias_rules',
            label: dicoLabelsService.getCollectionLabel('alias_rules', 'short'),
            type: 'dropdown',
            options: options.aliasOptions,
            value: marketplace.alias_rules,
            required: true,
            split: 1
          }
        ]
      }
    ]);
  }

  private getAliasForm(alias: Alias, globalSettings: GlobalSettings, options?): Form<Alias> {
    return new Form<Alias>([
      {
        name: 'alias',
        label: '<i class="fas fa-clone"></i> Alias',
        controls: [
          {
            name: 'alias_rules',
            label: dicoLabelsService.getCollectionLabel('alias_rules', 'short'),
            type: 'dropdown',
            options: options.aliasOptions,
            value: alias.alias_rules,
            required: true,
            split: 1
          }
        ]
      }
    ]);
  }

  private getOrderForm(order: Order, globalSettings: GlobalSettings, options?): Form<Order & OrderAddress> {
    return new Form<Order & OrderAddress>([
      {
        name: 'purchase',
        label: '<i class="fas fa-shopping-basket"></i> Achat',
        controls: [
          {
            name: 'marketplace_id',
            label: dicoLabelsService.getCollectionLabel('marketplace', 'short'),
            type: 'dropdown',
            options: options.marketplaces.map(mp => ({
              value: mp.id,
              label: mp.display_name
            })),
            value: order.marketplace_id,
            required: true,
            split: 3
          },
          {
            name: 'marketplace_order_id',
            label: dicoLabelsService.getCollectionLabel('marketplace_order_id', 'long'),
            type: 'text',
            value: order.marketplace_order_id,
            required: true,
            disabled: true,
            split: 3
          },
          {
            name: 'order_date',
            label: dicoLabelsService.getCollectionLabel('order_date', 'long'),
            type: 'datetime-local',
            value: order.order_date,
            required: true,
            split: 3
          },
          {
            name: 'price',
            label: dicoLabelsService.getCollectionLabel('price', 'long'),
            type: 'number',
            value: order.total_price,
            required: true,
            disabled: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            options: globalSettings.currencies.map((currency, index) => ({
              value: currency[0],
              label: `${currency[1]} (${currency[0]})`,
              indexWeight: index
            })),
            value: order.currency,
            required: true,
            split: 3
          },
          {
            name: 'payment_method',
            label: dicoLabelsService.getCollectionLabel('payment_method'),
            type: 'dropdown',
            options: globalSettings.payment_methods.map((payment_method, index) => ({
              value: payment_method[0],
              label: payment_method[1],
              indexWeight: index
            })),
            value: order.payment_method,
            split: 3
          }
        ]
      },
      {
        name: 'dispatch_info',
        label: '<i class="fas fa-dolly"></i> Expédition',
        controls: [
          {
            name: 'express',
            label: dicoLabelsService.getCollectionLabel('express', 'long'),
            type: 'toggle',
            value: order.express
          },
          {
            name: 'max_shipping_date',
            label: dicoLabelsService.getCollectionLabel('max_shipping_date', 'long'),
            type: 'datetime-local',
            value: order.max_shipping_date,
            required: true,
            disabled: true,
            split: 4
          },
          {
            name: 'shipping_date',
            label: dicoLabelsService.getCollectionLabel('shipping_date', 'long'),
            type: 'datetime-local',
            value: order.shipping_date,
            disabled: !options?.actionUpdateOrderShippingDate,
            split: 4
          },
          {
            name: 'shipping_fees',
            label: dicoLabelsService.getCollectionLabel('shipping_fees', 'long'),
            type: 'number',
            value: order.shipping_fees,
            unit: dicoLabelsService.getUnit('money'),
            required: true,
            split: 5
          }
        ]
      },
      {
        name: 'shipping_info',
        label: '<i class="fas fa-dolly"></i> Adresse de livraison',
        controls: [
          {
            name: 'shipping_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.last_name'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.first_name'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.company'),
            split: 3
          },
          {
            name: 'shipping_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.email'),
            split: 2
          },
          {
            name: 'shipping_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(order, 'shipping_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            split: 6
          },
          {
            name: 'shipping_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.phone'),
            split: 3
          },
          {
            name: 'shipping_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.street_1'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(order, 'shipping_info.street_2'),
            split: 2
          },
          {
            name: 'shipping_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.postal_code'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.city'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(order, 'shipping_info.country'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.relay_id',
            label: 'nothing',
            type: 'text',
            value: get(order, 'shipping_info.relay_id'),
            split: 3,
            hidden: true
          }
        ]
      },
      {
        name: 'billing_info',
        label: '<i class="fas fa-money-bill-alt"></i> Adresse de facturation',
        controls: [
          {
            name: 'same_address',
            label: dicoLabelsService.getCollectionLabel('same_address', 'long'),
            type: 'toggle',
            value: get(order, 'same_address'),
            afterChange: async (value: boolean, group) => {
              group.get('billing_info.last_name').hidden = value;
              group.get('billing_info.first_name').hidden = value;
              group.get('billing_info.company').hidden = value;
              group.get('billing_info.vat_number').hidden = value;
              group.get('billing_info.nif_code').hidden = value;
              group.get('billing_info.email').hidden = value;
              group.get('billing_info.phone').hidden = value;
              group.get('billing_info.street_1').hidden = value;
              group.get('billing_info.street_2').hidden = value;
              group.get('billing_info.postal_code').hidden = value;
              group.get('billing_info.city').hidden = value;
              group.get('billing_info.country').hidden = value;
              group.get('billing_info.dial_code').hidden = value;
            }
          },
          {
            name: 'billing_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(order, 'billing_info.last_name'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(order, 'billing_info.first_name'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(order, 'billing_info.company'),
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.vat_number',
            label: dicoLabelsService.getCollectionLabel('vat_number', 'long'),
            type: 'text',
            value: get(order, 'billing_info.vat_number'),
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.nif_code',
            label: dicoLabelsService.getCollectionLabel('nif_code', 'long'),
            type: 'text',
            value: get(order, 'billing_info.nif_code'),
            split: 3
          },
          {
            name: 'billing_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(order, 'billing_info.email'),
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(order, 'billing_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            hidden: get(order, 'same_address'),
            split: 6
          },
          {
            name: 'billing_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(order, 'billing_info.phone'),
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(order, 'billing_info.street_1'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(order, 'billing_info.street_2'),
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(order, 'billing_info.postal_code'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(order, 'billing_info.city'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(order, 'billing_info.country'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 3
          }
        ]
      }
    ]);
  }

  private getOrderCreationForm(order: Order, globalSettings: GlobalSettings, options): Form<Order & OrderAddress> {
    return new Form<Order & OrderAddress>([
      {
        name: 'purchase',
        label: '<i class="fas fa-shopping-basket"></i> Achat',
        controls: [
          {
            name: 'marketplace_id',
            label: dicoLabelsService.getCollectionLabel('marketplace', 'short'),
            type: 'dropdown',
            options: options.marketplaces.map(mp => ({
              value: mp.id,
              label: mp.display_name
            })),
            value: order?.marketplace_id,
            required: true,
            split: 3
          },
          {
            name: 'marketplace_order_id',
            label: dicoLabelsService.getCollectionLabel('marketplace_order_id', 'long'),
            type: 'text',
            value: order.marketplace_order_id,
            required: true,
            split: 3
          },
          {
            name: 'order_date',
            label: dicoLabelsService.getCollectionLabel('order_date', 'long'),
            type: 'datetime-local',
            value: order.order_date,
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            options: globalSettings.currencies.map((currency, index) => ({
              value: currency[0],
              label: `${currency[1]} (${currency[0]})`,
              indexWeight: index
            })),
            value: order.currency,
            required: true,
            split: 3
          },
          {
            name: 'payment_method',
            label: dicoLabelsService.getCollectionLabel('payment_method'),
            type: 'dropdown',
            options: globalSettings.payment_methods.map((payment_method, index) => ({
              value: payment_method[0],
              label: payment_method[1],
              indexWeight: index
            })),
            value: order.payment_method,
            split: 3
          },
          {
            name: 'is_bundle',
            label: 'Commande bundle',
            type: 'toggle',
            value: order.is_bundle,
            split: 1
          }
        ]
      },
      {
        name: 'dispatch_info',
        label: '<i class="fas fa-dolly"></i> Expédition',
        controls: [
          {
            name: 'express',
            label: dicoLabelsService.getCollectionLabel('express', 'long'),
            type: 'toggle',
            value: order.express,
            split: 2
          },
          {
            name: 'shipping_fees',
            label: dicoLabelsService.getCollectionLabel('shipping_fees', 'long'),
            type: 'number',
            value: order.shipping_fees,
            unit: dicoLabelsService.getUnit('money'),
            required: true,
            split: 2
          }
        ]
      },
      {
        name: 'shipping_info',
        label: '<i class="fas fa-dolly"></i> Adresse de livraison',
        controls: [
          {
            name: 'shipping_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.last_name'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.first_name'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.company'),
            split: 3
          },
          {
            name: 'shipping_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.email'),
            split: 2
          },
          {
            name: 'shipping_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(order, 'shipping_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            split: 6
          },
          {
            name: 'shipping_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.phone'),
            split: 3
          },
          {
            name: 'shipping_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.street_1'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(order, 'shipping_info.street_2'),
            split: 2
          },
          {
            name: 'shipping_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.postal_code'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(order, 'shipping_info.city'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(order, 'shipping_info.country'),
            required: true,
            split: 3
          }
        ]
      },
      {
        name: 'billing_info',
        label: '<i class="fas fa-money-bill-alt"></i> Adresse de facturation',
        controls: [
          {
            name: 'same_address',
            label: dicoLabelsService.getCollectionLabel('same_address', 'long'),
            type: 'toggle',
            value: get(order, 'same_address'),
            afterChange: async (value: boolean, group) => {
              group.get('billing_info.last_name').hidden = value;
              group.get('billing_info.first_name').hidden = value;
              group.get('billing_info.company').hidden = value;
              group.get('billing_info.vat_number').hidden = value;
              group.get('billing_info.nif_code').hidden = value;
              group.get('billing_info.email').hidden = value;
              group.get('billing_info.phone').hidden = value;
              group.get('billing_info.street_1').hidden = value;
              group.get('billing_info.street_2').hidden = value;
              group.get('billing_info.postal_code').hidden = value;
              group.get('billing_info.city').hidden = value;
              group.get('billing_info.country').hidden = value;
              group.get('billing_info.dial_code').hidden = value;
            }
          },
          {
            name: 'billing_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(order, 'billing_info.last_name'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(order, 'billing_info.first_name'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(order, 'billing_info.company'),
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.vat_number',
            label: dicoLabelsService.getCollectionLabel('vat_number', 'long'),
            type: 'text',
            value: get(order, 'billing_info.vat_number'),
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.nif_code',
            label: dicoLabelsService.getCollectionLabel('nif_code', 'long'),
            type: 'text',
            value: get(order, 'billing_info.nif_code'),
            split: 3
          },
          {
            name: 'billing_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(order, 'billing_info.email'),
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(order, 'billing_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            hidden: get(order, 'same_address'),
            split: 6
          },
          {
            name: 'billing_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(order, 'billing_info.phone'),
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(order, 'billing_info.street_1'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            value: get(order, 'billing_info.street_2'),
            maxlength: 35,
            hidden: get(order, 'same_address'),
            split: 2
          },
          {
            name: 'billing_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(order, 'billing_info.postal_code'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(order, 'billing_info.city'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 3
          },
          {
            name: 'billing_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(order, 'billing_info.country'),
            required: true,
            hidden: get(order, 'same_address'),
            split: 3
          }
        ]
      }
    ]);
  }

  private getCageForm(): Form<Cage> {
    return new Form<Cage>([
      {
        name: 'add',
        label: '<i class="fas fa-plus"></i> Ajouter',
        controls: [
          {
            name: 'scan',
            type: 'text'
          }
        ]
      },
      {
        name: 'list',
        label: '<i class="fas fa-list"></i> Liste des cartons',
        controls: [
          {
            name: 'test',
            label: 'test',
            type: 'text'
          }
        ]
      }
    ]);
  }

  private getCommissionForm(commission: Commission): Form<Commission> {
    return new Form<Commission>([
      {
        name: 'information',
        label: '<i class="fas fa-info"></i> Informations',
        controls: [
          {
            name: 'rate',
            label: dicoLabelsService.getCollectionLabel('rate'),
            type: 'number',
            value: commission.rate,
            split: 2
          },
          {
            name: 'fixed_amount_commission',
            label: dicoLabelsService.getCollectionLabel('fixed_amount'),
            type: 'number',
            value: commission.fixed_amount,
            split: 2
          }
        ]
      },
      {
        name: 'period',
        label: '<i class="fas fa-calendar"></i> Durée',
        controls: [
          {
            name: 'start_at',
            label: dicoLabelsService.getCollectionLabel('start_date'),
            type: 'datetime-local',
            value: commission.start_at,
            required: true
          },
          {
            name: 'end_at',
            label: dicoLabelsService.getCollectionLabel('end_date'),
            type: 'datetime-local',
            value: commission.end_at
          }
        ]
      }
    ]);
  }

  private getCommissionCreationForm(commission: Commission, globalSettings: GlobalSettings, options): Form<Commission> {
    return new Form<Commission>([
      {
        name: 'information',
        label: '<i class="fas fa-info"></i> Informations',
        controls: [
          {
            name: 'marketplace_id',
            label: dicoLabelsService.getCollectionLabel('marketplace'),
            type: 'dropdown',
            options: options.marketplaces.map(mp => ({
              value: mp.id,
              label: mp.display_name
            })),
            value: commission.marketplace?.id,
            required: true,
            split: 2
          },
          {
            name: 'country',
            label: dicoLabelsService.getCollectionLabel('country'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: commission.country,
            required: true,
            split: 2
          },
          {
            name: 'rate',
            label: dicoLabelsService.getCollectionLabel('rate'),
            type: 'number',
            value: commission.rate,
            split: 2
          },
          {
            name: 'fixed_amount',
            label: dicoLabelsService.getCollectionLabel('fixed_amount'),
            type: 'number',
            value: commission.fixed_amount,
            split: 2
          }
        ]
      },
      {
        name: 'period',
        label: '<i class="fas fa-calendar"></i> Durée',
        controls: [
          {
            name: 'start_at',
            label: dicoLabelsService.getCollectionLabel('start_date'),
            type: 'datetime-local',
            value: commission.start_at,
            required: true
          },
          {
            name: 'end_at',
            label: dicoLabelsService.getCollectionLabel('end_date'),
            type: 'datetime-local',
            value: commission.end_at
          }
        ]
      }
    ]);
  }

  getPartForm(part: Part, globalSettings: GlobalSettings): Form<Part> {
    return new Form<Part>([
      {
        name: 'part',
        controls: [
          {
            name: 'part_type',
            label: dicoLabelsService.getCollectionLabel('part_type'),
            type: 'text',
            value: PartType[part.part_type],
            disabled: true
          },
          {
            name: 'serial_number',
            label: dicoLabelsService.getCollectionLabel('serial_number'),
            type: 'text',
            value: part.serial_no,
            disabled: true,
            split: 2
          },
          {
            name: 'grade',
            label: dicoLabelsService.getCollectionLabel('grade'),
            type: 'text',
            value: part.grade,
            disabled: true,
            split: 2
          },
          {
            name: 'modele',
            label: dicoLabelsService.getCollectionLabel('modele'),
            type: 'text',
            value: part.modele,
            disabled: true,
            split: 2
          },
          {
            name: 'taille',
            label: dicoLabelsService.getCollectionLabel('taille'),
            type: 'text',
            value: part.taille,
            disabled: true,
            split: 2
          },

          {
            name: 'processeur',
            label: dicoLabelsService.getCollectionLabel('processeur'),
            type: 'text',
            value: part.processeur,
            disabled: true,
            split: 2
          },
          {
            name: 'color',
            label: dicoLabelsService.getCollectionLabel('color'),
            type: 'text',
            value: part.color,
            disabled: true,
            split: 2
          },
          {
            name: 'annee_modele',
            label: dicoLabelsService.getCollectionLabel('annee_modele'),
            options: globalSettings.compatibility_ecran.map(([id, data]) => ({
              value: id,
              label: data
              // label: `${data.modele.join(', ')} (${data.annee_debut} - ${data.annee_fin || '...'})`
            })),
            type: 'text',
            value: `${part.annee_debut} - ${part.annee_fin}`,
            disabled: true,
            split: 2
          }
        ]
      }
    ]);
  }

  getOrderLineExchangeCreationForm(
    orderLine: OrderLine,
    globalSettings: GlobalSettings
  ): Form<OrderLine & OrderAddress> {
    return new Form<OrderLine & OrderAddress>([
      {
        name: 'purchase',
        label: '<i class="fas fa-shopping-basket"></i> Achat',
        controls: [
          {
            name: 'order_line_type',
            label: dicoLabelsService.getCollectionLabel('order_line_type'),
            type: 'dropdown',
            options: globalSettings.order_line_types.map((type, index) => ({
              value: type[0],
              label: type[1],
              indexWeight: index
            })),
            value: orderLine.order_line_type,
            required: true,
            afterChange: async (value: string, group) => {
              // Adapt form depending on if the order is for a device or an accessory/part
              const isDeviceOrderLine = value === 'device';
              group.get('sku_marketplace').hidden = !isDeviceOrderLine;
              group.get('sku_okamac').label = isDeviceOrderLine
                ? dicoLabelsService.getCollectionLabel('sku_okamac', 'long')
                : dicoLabelsService.getCollectionLabel('accessory_part_id');
              group.get('sku_okamac').split = isDeviceOrderLine ? 2 : 1;
              group.get('sku_okamac').required = isDeviceOrderLine ? false : true;
            }
          },
          {
            name: 'order_id',
            label: dicoLabelsService.getCollectionLabel('order_id', 'short'),
            type: 'number',
            value: orderLine.order_id,
            required: true,
            split: 2
          },
          {
            name: 'marketplace_line_id',
            label: dicoLabelsService.getCollectionLabel('marketplace_order_id', 'long'),
            type: 'text',
            value: orderLine.marketplace_line_id,
            required: true,
            split: 2
          },
          {
            name: 'price',
            label: dicoLabelsService.getCollectionLabel('price', 'long'),
            type: 'number',
            value: orderLine.price,
            required: true,
            split: 3
          },
          {
            name: 'product_description',
            label: dicoLabelsService.getCollectionLabel('product_description', 'long'),
            type: 'text',
            value: orderLine.product_description
          },
          {
            name: 'sku_marketplace',
            label: dicoLabelsService.getCollectionLabel('sku_marketplace', 'long'),
            type: 'text',
            value: orderLine.sku_marketplace,
            required: true,
            split: 2
          },
          {
            name: 'sku_okamac',
            label: dicoLabelsService.getCollectionLabel('sku_okamac', 'long'),
            type: 'text',
            value: orderLine.sku_okamac,
            split: 2
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            value: orderLine.warranty,
            unit: dicoLabelsService.getUnit('month'),
            required: true,
            split: 2
          },
          {
            name: 'warranty_amount',
            label: dicoLabelsService.getCollectionLabel('warranty_amount', 'long'),
            type: 'number',
            value: orderLine.warranty_amount,
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'return_id',
            label: 'Id de retour',
            type: 'text',
            value: orderLine.return_id,
            split: 2
          }
        ]
      },
      {
        name: 'battery',
        label: '<i class="fas fa-battery-full"></i> Batterie neuve',
        controls: [
          {
            name: 'new_battery_required',
            label: dicoLabelsService.getCollectionLabel('new_battery_required', 'long'),
            type: 'toggle',
            value: get(orderLine, 'new_battery_required'),
            split: 2,
            afterChange: async (value: boolean, group) => {
              group.get('new_battery_price').hidden = value !== true;
              if (value !== true) {
                group.get('new_battery_price').value = null;
              }
            }
          },
          {
            name: 'new_battery_price',
            label: dicoLabelsService.getCollectionLabel('new_battery_price', 'long'),
            type: 'number',
            value: get(orderLine, 'new_battery_price'),
            unit: dicoLabelsService.getUnit('money'),
            required: true,
            split: 2
          }
        ]
      }
    ]);
  }

  private getOrderReturnCreationForm(
    orderReturn: OrderReturn,
    globalSettings: GlobalSettings,
    options?: { creditNoteReasons?: string[] }
  ): Form<OrderReturn & OrderAddress> {
    const creditNoteReasons = options?.creditNoteReasons || [];
    const creditNoteReasonLabels: Record<string, string> = {
      commercial_gesture: 'Dédommagement',
      delivery_delay: 'Retard de livraison',
      non_compliant_product: 'Produit non conforme',
      out_of_stock: 'Plus de stock',
      customs_fees: 'Frais de douane',
      customer_cancellation_before_shipment: 'Annulation client avant expédition',
      withdrawal_within_legal_period: 'Rétractation (délai légal)',
      item_not_received_lost: 'Article non reçu (perte)',
      withdrawal_outside_legal_period: 'Rétractation (hors délai)',
      exchange: 'Échange'
    };
    const isCreditNoteReasonVisible = (returnType: string | undefined) =>
      returnType === 'retractation' || returnType === 'exchange';
    return new Form<OrderReturn & OrderAddress>([
      {
        name: 'order_return_creation',
        controls: [
          {
            name: 'return_type',
            label: dicoLabelsService.getCollectionLabel('return_type'),
            type: 'dropdown',
            options: globalSettings.return_types.map((type, index) => ({
              value: type[0],
              label: type[1],
              indexWeight: index
            })),
            value: 'indeterminate',
            afterChange: async (value: string, group) => {
              group.get('upgrade_amount').hidden = value !== 'exchange';
              group.get('new_sku').hidden = value !== 'exchange';
              group.get('is_paid').hidden = value !== 'exchange';
              group.get('reasons').hidden = ['exchange', 'retractation', 'sav'].indexOf(value) < 0;
              if (value !== 'exchange') {
                group.get('upgrade_amount').value = null;
                group.get('new_sku').value = null;
                group.get('is_paid').value = false;
              }
              if (value == null) {
                group.get('reasons').value = [];
              }
              const showCreditNoteReason = isCreditNoteReasonVisible(value);
              group.get('credit_note_reason').hidden = !showCreditNoteReason;
              if (!showCreditNoteReason) group.get('credit_note_reason').value = null;
            }
          },
          {
            name: 'credit_note_reason',
            label: 'Raison de l’avoir',
            type: 'dropdown',
            value: orderReturn.credit_note_reason ?? null,
            options: creditNoteReasons
              .filter(r => r !== 'exchange')
              .map(r => ({ value: r, label: creditNoteReasonLabels[r] || r })),
            hidden: !isCreditNoteReasonVisible(orderReturn.return_type),
            required: true
          },
          {
            name: 'upgrade_amount',
            label: dicoLabelsService.getCollectionLabel('upgrade_amount'),
            type: 'number',
            value: get(orderReturn, 'upgrade_amount'),
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'is_paid',
            label: dicoLabelsService.getCollectionLabel('is_paid'),
            type: 'toggle',
            value: get(orderReturn, 'is_paid'),
            split: 2
          },
          {
            name: 'new_sku',
            label: dicoLabelsService.getCollectionLabel('new_sku'),
            type: 'text',
            value: get(orderReturn, 'new_sku')
          },
          {
            name: 'customer_return_date',
            label: dicoLabelsService.getCollectionLabel('customer_return_date', 'long'),
            type: 'date',
            value: get(orderReturn, 'customer_return_date')
          },
          {
            name: 'reasons',
            label: dicoLabelsService.getCollectionLabel('reasons', 'long'),
            type: 'multi-dropdown',
            options: globalSettings.customer_declared_faulties
              .map(category => ({
                value: category['value'],
                label: category['display_name']
              }))
              .sort((a, b) => a.label.localeCompare(b.label)),
            afterChange: async (value: string, group) => {
              const faulty = globalSettings.customer_declared_faulties;
              const types = [];
              if (value != undefined) {
                for (let i = 0; i < faulty.length; i++) {
                  if (value.includes(faulty[i]['value'])) {
                    const tmp = [...faulty[i]['types']];
                    types.push(...tmp.sort((a, b) => a.display_name.localeCompare(b.display_name)));
                  }
                }
              }
              const opt = types.map(el => ({
                value: el['value'],
                label: el['display_name']
              }));
              group.get('reasons_details').options = opt;
              group.get('reasons_details').hidden = types.length === 0;
            },
            enableAllField: false
          },
          {
            name: 'reasons_details',
            label: dicoLabelsService.getCollectionLabel('reasons_details', 'long'),
            type: 'multi-dropdown',
            value: [],
            hidden: true,
            enableAllField: false
          },
          {
            name: 'is_urgent',
            label: dicoLabelsService.getCollectionLabel('is_urgent'),
            type: 'toggle',
            value: get(orderReturn, 'is_urgent'),
            split: 2
          },
          {
            name: 'charger_included',
            label: dicoLabelsService.getCollectionLabel('charger_included'),
            type: 'toggle',
            value: get(orderReturn, 'charger_included'),
            split: 2
          }
        ]
      }
    ]);
  }

  private getOrderReturnForm(
    orderReturn: OrderReturn,
    globalSettings: GlobalSettings,
    options?: { creditNoteReasons?: string[] }
  ): Form<OrderReturn & OrderAddress> {
    const creditNoteReasons = options?.creditNoteReasons || [];
    const creditNoteReasonLabels: Record<string, string> = {
      commercial_gesture: 'Dédommagement',
      delivery_delay: 'Retard de livraison',
      non_compliant_product: 'Produit non conforme',
      out_of_stock: 'Plus de stock',
      customs_fees: 'Frais de douane',
      customer_cancellation_before_shipment: 'Annulation client avant expédition',
      withdrawal_within_legal_period: 'Rétractation (délai légal)',
      item_not_received_lost: 'Article non reçu (perte)',
      withdrawal_outside_legal_period: 'Rétractation (hors délai)',
      exchange: 'Échange'
    };
    const isCreditNoteReasonVisible = (returnType: string | undefined) =>
      returnType === 'retractation' || returnType === 'exchange';
    const shippingCompany = globalSettings.shipping_companies.find(sp => sp[0] === orderReturn.shipping_company);
    const shippingTypes = (shippingCompany && shippingCompany[1].types) || [];
    const status_code = globalSettings?.expedition_status?.find(
      sp => sp[0] === String(orderReturn.carrier_label_status)
    );
    const statusLabel = status_code ? status_code[1] : '';
    const receptionCompany = globalSettings.reception_companies.find(sp => sp[0] === orderReturn.reception_company);
    const receptionTypes = (receptionCompany && receptionCompany[1].types) || [];
    return new Form<OrderReturn & OrderAddress>([
      {
        name: 'order_return',
        label: '<i class="fas fa-arrow-down"></i> Retour',
        controls: [
          {
            name: 'return_type',
            label: dicoLabelsService.getCollectionLabel('return_type'),
            type: 'dropdown',
            options: globalSettings.return_types.map((type, index) => ({
              value: type[0],
              label: type[1],
              indexWeight: index
            })),
            value: orderReturn.return_type,
            afterChange: async (value: string, group) => {
              group.get('upgrade_amount').hidden = value !== 'exchange';
              group.get('new_sku').hidden = value !== 'exchange';
              group.get('is_paid').hidden = value !== 'exchange';
              if (value !== 'exchange') {
                group.get('upgrade_amount').value = null;
                group.get('new_sku').value = null;
                group.get('is_paid').value = false;
              }
              if (value == null || value == 'indeterminate') {
                group.get('reasons').hidden = true;
                group.get('reasons').value = [];
              } else {
                group.get('reasons').hidden = false;
              }
              const showCreditNoteReason = isCreditNoteReasonVisible(value);
              group.get('credit_note_reason').hidden = !showCreditNoteReason;
              if (!showCreditNoteReason) group.get('credit_note_reason').value = null;
            },
            disabled: !!orderReturn.reception_validated_at,
            split: 2
          },
          {
            name: 'date_reception',
            label: dicoLabelsService.getCollectionLabel('date_reception'),
            type: 'datetime-local',
            value: orderReturn.reception_validated_at,
            disabled: true,
            split: 2
          },
          {
            name: 'upgrade_amount',
            label: dicoLabelsService.getCollectionLabel('upgrade_amount'),
            type: 'number',
            value: get(orderReturn, 'upgrade_amount'),
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'is_paid',
            label: dicoLabelsService.getCollectionLabel('is_paid'),
            type: 'toggle',
            value: get(orderReturn, 'is_paid'),
            disabled: true,
            split: 2
          },
          {
            name: 'new_sku',
            label: dicoLabelsService.getCollectionLabel('new_sku'),
            type: 'text',
            value: get(orderReturn, 'new_sku'),
            split: 2,
            validators: [
              { func: v => !v || String(v).length === 35, message: 'Le SKU doit faire exactement 35 caractères' }
            ]
          },
          {
            name: 'customer_return_date',
            label: dicoLabelsService.getCollectionLabel('customer_return_date', 'long'),
            type: 'date',
            value: get(orderReturn, 'customer_return_date'),
            split: 2
          },
          {
            name: 'customer_accessories',
            label: 'Accessoires client déclarés',
            type: 'text',
            value:
              (orderReturn.customer_accessories || []).map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(', ') ||
              null,
            disabled: true,
            hidden: !orderReturn.customer_accessories?.length,
            split: 2
          },
          {
            name: 'credit_note_reason',
            label: 'Raison de l\u2019avoir',
            type: 'dropdown',
            value: orderReturn.credit_note_reason ?? null,
            options: creditNoteReasons
              .filter(r => r !== 'exchange')
              .map(r => ({ value: r, label: creditNoteReasonLabels[r] || r })),
            hidden: !isCreditNoteReasonVisible(orderReturn.return_type),
            required: true
          },
          {
            name: 'reasons',
            label: dicoLabelsService.getCollectionLabel('reasons', 'long'),
            type: 'multi-dropdown',
            value: orderReturn.reasons,
            options: globalSettings.customer_declared_faulties
              .map(category => ({
                value: category['value'],
                label: category['display_name']
              }))
              .sort((a, b) => a.label.localeCompare(b.label)),
            afterChange: async (value: string, group) => {
              const faulty = globalSettings.customer_declared_faulties;
              const types = [];
              if (value != undefined) {
                for (let i = 0; i < faulty.length; i++) {
                  if (value.includes(faulty[i]['value'])) {
                    const tmp = [...faulty[i]['types']];
                    types.push(...tmp.sort((a, b) => a.display_name.localeCompare(b.display_name)));
                  }
                }
              }
              const opt = types.map(el => ({
                value: el['value'],
                label: el['display_name']
              }));
              group.get('reasons_details').options = opt;
              group.get('reasons_details').hidden = types.length === 0;
            },
            enableAllField: false
          },
          {
            name: 'reasons_details',
            label: dicoLabelsService.getCollectionLabel('reasons_details', 'long'),
            type: 'multi-dropdown',
            value: orderReturn.reasons_details,
            hidden: true,
            enableAllField: false
          },
          {
            name: 'accessory_problems',
            label: dicoLabelsService.getCollectionLabel('accessory_problems'),
            type: 'multi-dropdown',
            value: orderReturn.accessory_problems,
            options: globalSettings.accessory_problems.map(faulty => ({
              value: faulty[0],
              label: faulty[1]
            })),
            enableAllField: false
          },
          {
            name: 'quality_defaults',
            label: dicoLabelsService.getCollectionLabel('quality_defaults'),
            type: 'multi-dropdown',
            value: orderReturn.quality_defaults,
            options: globalSettings.quality_defaults.map(faulty => ({
              value: faulty[0],
              label: faulty[1]
            })),
            enableAllField: false
          },
          {
            name: 'is_urgent',
            label: dicoLabelsService.getCollectionLabel('is_urgent'),
            type: 'toggle',
            value: get(orderReturn, 'is_urgent'),
            split: 2
          },
          {
            name: 'charger_included',
            label: dicoLabelsService.getCollectionLabel('charger_included'),
            type: 'toggle',
            value: get(orderReturn, 'charger_included'),
            split: 2
          }
        ]
      },
      {
        name: 'orderLine',
        label: '<i class="fas fa-shopping-basket"></i> Commande',
        controls: [
          {
            name: 'marketplace',
            label: dicoLabelsService.getCollectionLabel('marketplace', 'short'),
            type: 'text',
            value: orderReturn.order_line.order.marketplace.display_name,
            disabled: true,
            split: 2
          },
          {
            name: 'marketplace_order_id',
            label: dicoLabelsService.getCollectionLabel('marketplace_order_id', 'long'),
            type: 'text',
            value: orderReturn.order_line.order.marketplace_order_id,
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'marketplace_line_id',
            label: dicoLabelsService.getCollectionLabel('marketplace_line_id', 'long'),
            type: 'text',
            value: orderReturn.order_line.marketplace_line_id,
            required: true,
            disabled: true
          },
          {
            name: 'order_date',
            label: dicoLabelsService.getCollectionLabel('order_date', 'long'),
            type: 'datetime-local',
            value: orderReturn.order_line.order.order_date,
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            value: orderReturn.order_line.warranty,
            unit: dicoLabelsService.getUnit('month'),
            required: true,
            disabled: true,
            split: 2
          },
          {
            name: 'sku_okamac',
            label: dicoLabelsService.getCollectionLabel('sku_okamac', 'long'),
            type: 'text',
            value: orderReturn.order_line.sku_okamac,
            disabled: true,
            split: 2
          },
          {
            name: 'price',
            label: dicoLabelsService.getCollectionLabel('price', 'long'),
            type: 'number',
            value: orderReturn.order_line.price,
            unit: orderReturn.order_line.order.currency,
            required: true,
            disabled: true,
            split: 2
          }
        ]
      },
      {
        name: 'return_info',
        label: '<i class="fas fa-dolly"></i> Réception',
        controls: [
          {
            name: 'reception_company',
            label: dicoLabelsService.getCollectionLabel('shipping_company', 'long'),
            type: 'dropdown',
            options: globalSettings.reception_companies.map((sc, index) => ({
              value: sc[0],
              label: sc[1].display_name,
              indexWeight: index
            })),
            value: orderReturn.reception_company,
            afterChange: async (value: string, group) => {
              const company = globalSettings.reception_companies.find(sp => sp[0] === value);
              const types = (company && company[1].types) || [];
              const options = types.map(type => ({
                value: type,
                label: type
              }));
              group.get('reception_type').options = options;
            },
            disabled: true,
            split: 2
          },
          {
            name: 'reception_type',
            label: dicoLabelsService.getCollectionLabel('shipping_type', 'long'),
            type: 'dropdown',
            options: receptionTypes.map(type => ({
              value: type,
              label: type
            })),
            value: orderReturn.reception_type,
            disabled: true,
            split: 2
          },
          {
            name: 'reception_date',
            label: dicoLabelsService.getCollectionLabel('print_return_label_date'),
            type: 'datetime-local',
            value: orderReturn.reception_date,
            disabled: true,
            split: 2
          },
          {
            name: 'reception_tracking_number',
            label: dicoLabelsService.getCollectionLabel('shipping_tracking_number', 'long'),
            type: 'text',
            value: orderReturn.reception_tracking_number,
            disabled: true,
            split: 2
          }
        ]
      },
      {
        name: 'dispatch_info',
        label: '<i class="fas fa-dolly"></i> Expédition',
        controls: [
          {
            name: 'shipping_company',
            label: dicoLabelsService.getCollectionLabel('shipping_company', 'long'),
            type: 'dropdown',
            options: globalSettings.shipping_companies.map((sc, index) => ({
              value: sc[0],
              label: sc[1].display_name,
              indexWeight: index
            })),
            value: orderReturn.shipping_company,
            afterChange: async (value: string, group) => {
              const company = globalSettings.shipping_companies.find(sp => sp[0] === value);
              const types = (company && company[1].types) || [];
              const options = types.map(type => ({
                value: type,
                label: type
              }));
              group.get('shipping_type').options = options;
            },
            split: 2
          },
          {
            name: 'shipping_type',
            label: dicoLabelsService.getCollectionLabel('shipping_type', 'long'),
            type: 'dropdown',
            options: shippingTypes.map(type => ({
              value: type,
              label: type
            })),
            value: orderReturn.shipping_type,
            split: 2
          },
          {
            name: 'max_shipping_date',
            label: dicoLabelsService.getCollectionLabel('max_shipping_date', 'long'),
            type: 'datetime-local',
            value: orderReturn.max_shipping_date,
            disabled: true,
            split: 2
          },
          {
            name: 'shipping_date',
            label: dicoLabelsService.getCollectionLabel('shipping_date', 'long'),
            type: 'datetime-local',
            value: orderReturn.shipping_date,
            disabled: true,
            split: 2
          },
          {
            name: 'shipping_tracking_number',
            label: dicoLabelsService.getCollectionLabel('shipping_tracking_number', 'long'),
            type: 'text',
            value: orderReturn.shipping_tracking_number,
            disabled: true,
            split: 2
          },
          {
            name: 'expedition_status',
            label: dicoLabelsService.getCollectionLabel('expedition_status'),
            type: 'text',
            value: statusLabel,
            required: false,
            split: 2
          },
          {
            name: 'manual_tracking_number',
            label: dicoLabelsService.getCollectionLabel('manual_tracking_number'),
            type: 'text',
            value: orderReturn.manual_tracking_number,
            required: false,
            split: 2
          }
        ]
      },
      {
        name: 'shipping_info',
        label: '<i class="fas fa-dolly"></i> Adresse de livraison',
        controls: [
          {
            name: 'shipping_info.last_name',
            label: dicoLabelsService.getCollectionLabel('last_name', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.last_name'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.first_name',
            label: dicoLabelsService.getCollectionLabel('first_name', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.first_name'),
            required: true,
            split: 2
          },
          {
            name: 'shipping_info.company',
            label: dicoLabelsService.getCollectionLabel('company', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.company')
          },
          {
            name: 'shipping_info.email',
            label: dicoLabelsService.getCollectionLabel('email', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.email'),
            split: 3
          },
          {
            name: 'shipping_info.dial_code',
            label: dicoLabelsService.getCollectionLabel('dial_code'),
            type: 'dropdown',
            value: get(orderReturn, 'shipping_info.dial_code'),
            options: globalSettings.countries.map((phone_label, index) => ({
              value: phone_label[2],
              label: dicoLabelsService.decodeFlag(phone_label[0]) + ' ' + phone_label[2],
              indexWeight: index
            })),
            split: 4
          },
          {
            name: 'shipping_info.phone',
            label: dicoLabelsService.getCollectionLabel('phone', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.phone'),
            split: 3
          },
          {
            name: 'shipping_info.street_1',
            label: dicoLabelsService.getCollectionLabel('street_1', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.street_1'),
            required: true
          },
          {
            name: 'shipping_info.street_2',
            label: dicoLabelsService.getCollectionLabel('street_2', 'long'),
            type: 'text',
            maxlength: 35,
            value: get(orderReturn, 'shipping_info.street_2')
          },
          {
            name: 'shipping_info.postal_code',
            label: dicoLabelsService.getCollectionLabel('postal_code', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.postal_code'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.city',
            label: dicoLabelsService.getCollectionLabel('city', 'long'),
            type: 'text',
            value: get(orderReturn, 'shipping_info.city'),
            required: true,
            split: 3
          },
          {
            name: 'shipping_info.country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            options: globalSettings.countries.map((country, index) => ({
              value: country[0],
              label: country[1],
              indexWeight: index
            })),
            value: get(orderReturn, 'shipping_info.country'),
            required: true,
            split: 3
          }
        ]
      },
      {
        name: 'status',
        label: '<i class="fas fa-headset"></i> Demandes particulières',
        controls: [
          {
            name: 'out_of_stock',
            label: dicoLabelsService.getCollectionLabel('out_of_stock', 'long'),
            type: 'toggle',
            value: get(orderReturn, 'out_of_stock'),
            split: 2
          }
        ]
      }
    ]);
  }

  private getProductionOrderCreationForm(productionOrder: ProductionOrder): Form<ProductionOrder> {
    return new Form<ProductionOrder>([
      {
        name: 'production-order-line-creation',
        label: '<i class="fas fa-microchip"></i> Ordinateur à fabriquer',
        controls: [
          {
            name: 'sku',
            label: dicoLabelsService.getCollectionLabel('sku', 'long'),
            type: 'text',
            value: productionOrder.sku,
            required: true
          }
        ]
      }
    ]);
  }

  private getProductionOrderForm(productionOrder: ProductionOrder): Form<ProductionOrder> {
    return new Form<ProductionOrder>([
      {
        name: 'production-order',
        label: 'Ordre de fabrication',
        controls: [
          {
            name: 'sku',
            label: dicoLabelsService.getCollectionLabel('sku', 'long'),
            type: 'text',
            value: productionOrder.sku,
            disabled: true,
            required: true
          },
          {
            name: 'product_description',
            label: dicoLabelsService.getCollectionLabel('product_description', 'long'),
            type: 'text',
            value: productionOrder.product_description,
            disabled: true,
            required: true
          }
        ]
      }
    ]);
  }

  getOrderReturnReceptionForm(
    globalSettings: GlobalSettings,
    orderReturn: OrderReturn
  ): Form<OrderReturnReceptionOptions> {
    return new Form<OrderReturnReceptionOptions>([
      {
        name: 'reception-options',
        controls: [
          {
            name: 'litigation_reasons',
            label: dicoLabelsService.getCollectionLabel('litigation_reasons'),
            type: 'switch-list',
            value: orderReturn.litigation_reasons,
            options: globalSettings.litigation_reasons.map((reason, index) => ({
              value: reason[0],
              label: reason[1],
              indexWeight: index
            }))
          },
          {
            name: 'charger_included',
            label: dicoLabelsService.getCollectionLabel('charger_included'),
            value: orderReturn.charger_included,
            type: 'toggle',
            split: 2
          },
          {
            name: 'comment',
            label: dicoLabelsService.getCollectionLabel('comment', 'long'),
            type: 'text-multiline',
            required: false
          }
        ]
      }
    ]);
  }

  getOrderReturnExchangeForm(orderReturn: OrderReturn, options): Form<OrderReturnExchangeOptions> {
    const { UserCanValidateOrderReturnPayment } = options;
    return new Form<OrderReturnExchangeOptions>([
      {
        name: 'exchange-options',
        controls: [
          {
            name: 'upgrade_amount',
            label: dicoLabelsService.getCollectionLabel('upgrade_amount'),
            type: 'number',
            value: get(orderReturn, 'upgrade_amount'),
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'new_sku',
            label: dicoLabelsService.getCollectionLabel('new_sku'),
            type: 'text',
            value: get(orderReturn, 'new_sku'),
            split: 2,
            validators: [
              { func: v => !v || String(v).length === 35, message: 'Le SKU doit faire exactement 35 caractères' }
            ]
          },
          {
            name: 'is_paid',
            label: dicoLabelsService.getCollectionLabel('is_paid'),
            type: 'toggle',
            hidden: !UserCanValidateOrderReturnPayment,
            value: get(orderReturn, 'is_paid'),
            split: 2
          }
        ]
      }
    ]);
  }

  private getPurchaseForm(purchase: Purchase, settings: GlobalSettings): Form<Purchase> {
    if (!settings) return;
    if (!purchase.supplier.ribs) return;

    const statusIndex = ['negotiation', 'shipping', 'received', 'control', 'rma', 'validated'].indexOf(
      purchase.purchase_status
    );

    const form = new Form<Purchase>([
      {
        name: 'supplier',
        label: '<i class="fas fa-user-circle"></i> Fournisseur',
        controls: [
          {
            name: 'supplier_id',
            label: dicoLabelsService.getCollectionLabel('last_name', 'short'),
            type: 'dropdown',
            options: [],
            value: purchase.supplier?.id,
            disabled: statusIndex > 0,
            required: true,
            split: 2,
            afterChange: (value: string, group) => {
              const options = group.get('supplier_id').options;
              if (!options?.length) {
                // Options init
                return suppliersService.order_list({ filters: { category: purchase.item_type } }).then(resp => {
                  const suppliers = resp.values || [];
                  if (!suppliers) throw new Error('Impossible de récupérer la liste des fournisseurs');
                  group.get('supplier_id').options = suppliers?.map(value => ({
                    value: value.id,
                    label: value.display_name,
                    details: value
                  }));
                });
              } else {
                const option = options.find(opt => opt.value === value);
                if (!group.get('supplier_country').value) {
                  group.get('supplier_country').value = option?.details?.country;
                }
                if (!group.get('currency').value) {
                  group.get('currency').value = option?.details?.currency;
                }
                if (!group.get('tax_scheme').value) {
                  group.get('tax_scheme').value = option?.details?.tax_scheme;
                }
                if (!group.get('payment_timing').value) {
                  group.get('payment_timing').value = option?.details?.payment_timing;
                  group.get('payment_delay').value = option?.details?.payment_delay;
                  group.get('payment_delay').hidden = option?.details?.payment_timing !== 'delay';
                }
              }
            }
          },
          {
            name: 'supplier_country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'text',
            value: purchase.supplier_country,
            required: true,
            split: 2
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            value: purchase.currency,
            disabled: false,
            options: [
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'usd', label: 'USD' }
            ],
            required: true,
            split: 2
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'short'),
            value: purchase.tax_scheme,
            type: 'dropdown',
            options: settings.schemes.map((scheme, index) => ({
              value: scheme[0],
              label: scheme[1],
              indexWeight: index
            })),
            disabled: false,
            required: true,
            split: 2
          },
          {
            name: 'reference_internal',
            label: dicoLabelsService.getCollectionLabel('reference_internal'),
            type: 'text',
            value: purchase.reference_internal,
            disabled: true,
            split: 3
          },
          {
            name: 'reference_supplier',
            label: dicoLabelsService.getCollectionLabel('reference_supplier', 'long'),
            type: 'text',
            value: purchase.reference_supplier,
            disabled: false,
            required: false,
            split: 3
          },
          {
            name: 'incoterm',
            label: dicoLabelsService.getCollectionLabel('incoterm', 'short'),
            value: purchase.incoterm,
            type: 'dropdown',
            options: ['CIF', 'CIP', 'CFR', 'CPT', 'DAP', 'DDP', 'DPU', 'EXW', 'FAS', 'FCA', 'FOB'].map(
              (item, index) => ({
                value: item,
                label: item,
                indexWeight: index
              })
            ),
            disabled: false,
            required: false,
            split: 3
          },
          ...(statusIndex === 0
            ? ([
                {
                  name: 'payment_timing',
                  label: dicoLabelsService.getCollectionLabel('payment_timing', 'long'),
                  type: 'dropdown',
                  value: purchase.payment_timing || purchase.supplier?.payment_timing,
                  options: [
                    { value: 'before_reception', label: 'Avant expédition' },
                    { value: 'at_reception', label: 'A la livraison' },
                    { value: 'delay', label: 'A X jours' }
                  ],
                  required: true,
                  split: 3,
                  afterChange: async (value: string, group) => {
                    group.get('payment_delay').hidden = value !== 'delay';
                    group.get('payment_timing').split = value !== 'delay' ? 3 : 2;
                    group.get('purchase_types').split = value !== 'delay' ? 3 : 2;
                    group.get('supplier_order').split = value !== 'delay' ? 3 : 2;
                  }
                },
                {
                  name: 'payment_delay',
                  label: dicoLabelsService.getCollectionLabel('payment_delay', 'long'),
                  type: 'number',
                  value: purchase.payment_delay || purchase.supplier?.payment_delay,
                  required: true,
                  split: 2
                }
              ] as FormControlRaw<Purchase>[])
            : []),
          {
            name: 'purchase_types',
            label: dicoLabelsService.getCollectionLabel('purchase_types'),
            type: 'dropdown',
            value: purchase.purchase_types,
            options: settings.purchase_types.map(type => ({
              value: type[0],
              label: type[1]
            })),
            required: true,
            split: statusIndex === 0 ? 3 : 2
          },
          {
            name: 'supplier_order',
            label: dicoLabelsService.getCollectionLabel('supplier_order'),
            type: 'text',
            value: purchase.supplier_order,
            required: false,
            split: statusIndex === 0 ? 3 : 2
          }
        ]
      },
      {
        name: 'purchase',
        label: '<i class="fas fa-money-check-alt"></i> Paiement',
        controls: [
          {
            name: 'payment_scheduled_timing',
            label: dicoLabelsService.getCollectionLabel('payment_scheduled_timing', 'long'),
            type: 'dropdown',
            options: [
              { value: 'immediate', label: 'Immédiat' },
              { value: 'automatic', label: 'Prélèvement automatique' },
              { value: 'date', label: 'Choisir une date' }
            ],
            value: purchase.payment_scheduled_timing,
            split: 2
          },
          {
            name: 'delivery_expected_date',
            label: dicoLabelsService.getCollectionLabel('delivery_expected_date', 'long'),
            type: 'date',
            value: datesService.format(purchase.delivery_expected_date, 'YYYY-MM-DD'),
            split: 2
          },
          {
            name: 'payment_scheduled_at',
            label: dicoLabelsService.getCollectionLabel('payment_scheduled_at', 'long'),
            type: 'datetime-local',
            value: purchase.payment_scheduled_at,
            disabled: true,
            split: 2
          },
          {
            name: 'payment_scheduled_by',
            label: dicoLabelsService.getCollectionLabel('payment_scheduled_by', 'long'),
            type: 'text',
            value: dicoLabelsService.formatName(purchase.payment_scheduled_by),
            disabled: true,
            split: 2
          },
          {
            name: 'payment_transfer_date',
            label: dicoLabelsService.getCollectionLabel('payment_transfer_date', 'long'),
            type: 'datetime-local',
            value: purchase.payment_transfer_date,
            split: 2
          },
          {
            name: 'payment_method',
            label: dicoLabelsService.getCollectionLabel('payment_method'),
            type: 'dropdown',
            value: purchase.payment_method,
            split: 2,
            options: [
              { value: 'credit_card', label: 'Carte bancaire' },
              { value: 'paypal', label: 'Paypal' },
              { value: 'bank_transfer', label: 'Virement' }
            ],
            afterChange: async (value: string, group) => {
              group.get('payment_supplier_rib_id').hidden = value !== 'bank_transfer';
            }
          },
          {
            name: 'payment_supplier_rib_id',
            label: dicoLabelsService.getCollectionLabel('payment_supplier_rib_id', 'long'),
            type: 'text',
            // options: purchase.supplier?.ribs?.map(rib => ({
            //   value: rib.id,
            //   label: rib.bic
            // })),
            value: purchase.supplier.ribs.map(rib => rib.bic).join(', ')
          },
          {
            name: 'payment_effective_at',
            label: dicoLabelsService.getCollectionLabel('payment_effective_at', 'long'),
            type: 'datetime-local',
            value: purchase.payment_effective_at,
            disabled: true,
            split: 2
          },
          {
            name: 'payment_effective_by',
            label: dicoLabelsService.getCollectionLabel('payment_effective_by', 'long'),
            type: 'text',
            value: dicoLabelsService.formatName(purchase.payment_effective_by),
            disabled: true,
            split: 2
          },
          {
            name: 'shipping_carrier',
            label: dicoLabelsService.getCollectionLabel('shipping_carrier', 'long'),
            type: 'text',
            value: purchase.shipping_carrier,
            split: 2
          },
          {
            name: 'shipping_tracking_number',
            label: dicoLabelsService.getCollectionLabel('shipping_tracking_number', 'long'),
            type: 'text',
            value: purchase.shipping_tracking_number,
            split: 2
          },
          {
            name: 'shipping_cost',
            label: dicoLabelsService.getCollectionLabel('shipping_cost', 'long'),
            type: 'number',
            value: purchase.shipping_cost,
            step: '0.01',
            unit: dicoLabelsService.getUnit('money'),
            split: 2
          },
          {
            name: 'nb_expected_packaging',
            label: dicoLabelsService.getCollectionLabel('nb_expected_packaging'),
            type: 'number',
            value: purchase.nb_expected_packaging,
            split: 2
          },
          ...(purchase.items
            ? ([
                ...(purchase.item_type === 'spare'
                  ? [
                      {
                        name: 'spare_types',
                        label: dicoLabelsService.getCollectionLabel('spare_types', 'long'),
                        type: 'display',
                        value: dicoLabelsService.formatPurchaseItemShare(
                          purchase.items,
                          'spare_type',
                          settings?.spare_types
                        )
                      }
                    ]
                  : []),
                ...(purchase.item_type === 'consumable'
                  ? [
                      {
                        name: 'consumable_types',
                        label: dicoLabelsService.getCollectionLabel('consumable_types', 'long'),
                        type: 'display',
                        value: dicoLabelsService.formatPurchaseItemShare(
                          purchase.items,
                          'consumable_type',
                          settings?.consumable_types
                        )
                      }
                    ]
                  : [])
              ] as FormControlRaw<Purchase>[])
            : [])
        ]
      },
      ...(statusIndex >= 2
        ? [
            {
              name: 'reception',
              label: 'Réception',
              controls: [
                {
                  name: 'reception_status',
                  label: dicoLabelsService.getCollectionLabel('reception_status', 'short'),
                  type: 'display',
                  value: purchase.quantity_received === purchase.quantity ? 'Complète' : 'Partielle',
                  disabled: true,
                  split: 2
                },
                {
                  name: 'reception_at',
                  label: dicoLabelsService.getCollectionLabel('reception_at'),
                  type: 'datetime-local',
                  value: purchase.reception_at,
                  split: 2
                },
                {
                  name: 'quantity_received',
                  label: dicoLabelsService.getCollectionLabel('quantity_received', 'long'),
                  type: 'number',
                  value: purchase.quantity_received,
                  required: true,
                  split: 2
                },
                {
                  name: 'quantity_control',
                  label: dicoLabelsService.getCollectionLabel('quantity_control'),
                  type: 'number',
                  value: purchase.quantity_control,
                  required: true,
                  disabled: true,
                  split: 2
                }
              ] as FormControlRaw<Purchase>[]
            }
          ]
        : []),
      ...(statusIndex > 3 && purchase.item_type === 'device'
        ? [
            {
              name: 'control',
              label: 'Contrôle',
              controls: [
                {
                  name: 'control_started_at',
                  label: dicoLabelsService.getCollectionLabel('control_started_at', 'long'),
                  type: 'datetime-local',
                  value: purchase.control_started_at,
                  disabled: true,
                  required: true
                },
                {
                  name: 'control_started_by',
                  label: dicoLabelsService.getCollectionLabel('control_started_by', 'long'),
                  type: 'text',
                  value: dicoLabelsService.formatName(purchase.control_started_by),
                  disabled: true,
                  required: true
                }
              ] as FormControlRaw<Purchase>[]
            }
          ]
        : []),
      {
        name: 'files',
        controls: [
          {
            name: 'initial_supplier_file',
            label: dicoLabelsService.getCollectionLabel('file', 'long'),
            type: 'file',
            value: purchase.initial_supplier_file,
            disabled: !purchase.id
          },
          {
            name: 'analysis_supplier_file',
            label: dicoLabelsService.getCollectionLabel('analysis_supplier_file', 'long'),
            type: 'file',
            value: purchase.analysis_supplier_file,
            disabled: !purchase.id
          },
          {
            name: 'payment_confirmation_file',
            label: dicoLabelsService.getCollectionLabel('payment_confirmation_file', 'long'),
            type: 'file',
            value: purchase.payment_confirmation_file
          },
          {
            name: 'credit_note',
            label: dicoLabelsService.getCollectionLabel('credit_note'),
            type: 'file',
            value: purchase.credit_note
          },
          {
            name: 'rma_file',
            label: dicoLabelsService.getCollectionLabel('rma_file', 'long'),
            type: 'file',
            value: purchase.rma_file
          }
        ]
      }
    ]);

    return form;
  }

  private getSupplierForm(supplier: Supplier, globalSettings: GlobalSettings): Form<Supplier> {
    if (!globalSettings) return;

    return new Form<Supplier>([
      {
        name: 'supplier',
        controls: [
          {
            name: 'display_name',
            label: dicoLabelsService.getCollectionLabel('display_name', 'long'),
            type: 'text',
            value: supplier.display_name,
            required: true,
            split: 1
          },
          {
            name: 'category',
            label: dicoLabelsService.getCollectionLabel('category', 'long'),
            type: 'dropdown',
            options: [
              { value: 'device', label: 'Ordinateurs' },
              { value: 'spare', label: 'Pièces détachées' },
              { value: 'consumable', label: 'Emballages' },
              { value: 'other', label: 'Autre' }
            ],
            value: supplier.category,
            required: true,
            split: 2
          },
          {
            name: 'supplier_grade_default',
            label: dicoLabelsService.getCollectionLabel('supplier_grade_default', 'long'),
            type: 'dropdown',
            options: supplier.supplier_grades.map(grade => ({
              value: grade.name,
              label: grade.name
            })),
            value: supplier.supplier_grade_default,
            split: 2
          },
          {
            name: 'sub_categories',
            label: dicoLabelsService.getCollectionLabel('sub_categories', 'long'),
            type: 'multi-dropdown',
            options: [
              { value: 'accessory', label: 'Accessoire' },
              { value: 'battery', label: 'Batterie' },
              { value: 'charger', label: 'Chargeur' },
              { value: 'hard_drive', label: 'Disque dur' },
              { value: 'ram', label: 'RAM' }
            ],
            value: supplier.sub_categories,
            split: 2
          },
          {
            name: 'country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'dropdown',
            value: supplier.country,
            options: globalSettings.countries.map(country => ({
              value: country[0],
              label: country[1]
            })),
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            value: supplier.currency,
            options: [
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'usd', label: 'USD' }
            ],
            required: true,
            split: 3
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'long'),
            value: supplier.tax_scheme,
            type: 'dropdown',
            options: globalSettings.schemes.map((scheme, index) => ({
              value: scheme[0],
              label: scheme[1],
              indexWeight: index
            })),
            required: true,
            split: 3
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            value: supplier.warranty,
            unit: dicoLabelsService.getUnit('day'),
            required: true,
            split: 2
          },
          {
            name: 'warranty_types',
            label: dicoLabelsService.getCollectionLabel('warranty_types'),
            type: 'dropdown',
            value: supplier.warranty_types,
            options: globalSettings.warranty_types.map(rma_type => ({
              value: rma_type[0],
              label: rma_type[1]
            })),
            split: 2
          },
          {
            name: 'payment_timing',
            label: dicoLabelsService.getCollectionLabel('payment_timing', 'long'),
            type: 'dropdown',
            value: supplier.payment_timing,
            options: [
              { value: 'before_reception', label: 'Avant expédition' },
              { value: 'at_reception', label: 'A la livraison' },
              { value: 'delay', label: 'A X jours' }
            ],
            required: true,
            afterChange: async (value: string, group) => {
              group.get('payment_delay').hidden = value !== 'delay';
            },
            split: 2
          },
          {
            name: 'payment_delay',
            label: dicoLabelsService.getCollectionLabel('payment_delay', 'long'),
            type: 'number',
            value: supplier.payment_delay,
            required: true,
            split: 2
          },
          {
            name: 'ratio_ncf',
            label: dicoLabelsService.getCollectionLabel('ratio_ncf'),
            type: 'number',
            value: supplier.ratio_ncf,
            required: true,
            split: 2
          },
          {
            name: 'commission',
            label: 'Commission (%)',
            type: 'number',
            value: supplier.commission,
            split: 2
          },
          {
            name: 'comment',
            label: dicoLabelsService.getCollectionLabel('comment', 'long'),
            type: 'text-multiline',
            value: supplier.comment,
            split: 1
          }
        ]
      }
    ]);
  }

  private getCageCarrierLabelDisplayForm(
    cageCarrierLabel: CageCarrierLabel,
    globalSettings: GlobalSettings
  ): Form<CageCarrierLabel> {
    if (!globalSettings) return;

    return new Form<CageCarrierLabel>([
      {
        name: 'row',
        controls: [
          {
            name: 'serial_number',
            type: 'text',
            value: cageCarrierLabel.item_code,
            required: true,
            split: 2
          },
          {
            name: 'tracking_number',
            type: 'text',
            value: cageCarrierLabel.order_id,
            required: true,
            split: 2
          }
        ]
      }
    ]);
  }

  private getSupplierDisplayForm(supplier: Supplier, globalSettings: GlobalSettings): Form<Supplier> {
    if (!globalSettings) return;

    return new Form<Supplier>([
      {
        name: 'name',
        controls: [
          {
            name: 'display_name',
            label: dicoLabelsService.getCollectionLabel('display_name', 'long'),
            type: 'text',
            value: supplier.display_name,
            required: true,
            split: 1
          }
        ]
      },
      {
        name: 'category',
        controls: [
          {
            name: 'category',
            label: dicoLabelsService.getCollectionLabel('category', 'long'),
            type: 'dropdown',
            options: [
              { value: 'device', label: 'Ordinateurs' },
              { value: 'spare', label: 'Pièces détachées' },
              { value: 'consumable', label: 'Emballages' },
              { value: 'other', label: 'Autre' }
            ],
            value: supplier.category,
            required: true,
            afterChange: async (value: string, group) => {
              group.get('category').split = value === 'spare' ? 2 : 1;
              group.get('sub_categories').hidden = value !== 'spare';
            },
            split: supplier.category === 'spare' ? 2 : 1
          },
          {
            name: 'sub_categories',
            label: dicoLabelsService.getCollectionLabel('sub_categories', 'long'),
            type: 'multi-dropdown',
            options: [
              { value: 'accessory', label: 'Accessoire' },
              { value: 'battery', label: 'Batterie' },
              { value: 'charger', label: 'Chargeur' },
              { value: 'hard_drive', label: 'Disque dur' },
              { value: 'ram', label: 'RAM' }
            ],
            value: supplier.sub_categories,
            split: 2
          }
        ]
      },
      {
        name: 'supplier',
        controls: [
          {
            name: 'country',
            label: dicoLabelsService.getCollectionLabel('country', 'long'),
            type: 'text',
            value: supplier.country,
            required: true,
            split: 3
          },
          {
            name: 'currency',
            label: dicoLabelsService.getCollectionLabel('currency', 'long'),
            type: 'dropdown',
            value: supplier.currency,
            options: [
              { value: 'eur', label: 'EUR' },
              { value: 'gbp', label: 'GBP' },
              { value: 'usd', label: 'USD' }
            ],
            required: true,
            split: 3
          },
          {
            name: 'tax_scheme',
            label: dicoLabelsService.getCollectionLabel('tax_scheme', 'long'),
            value: supplier.tax_scheme,
            type: 'dropdown',
            options: globalSettings.schemes.map((scheme, index) => ({
              value: scheme[0],
              label: scheme[1],
              indexWeight: index
            })),
            required: true,
            split: 3
          },
          {
            name: 'warranty',
            label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
            type: 'number',
            value: supplier.warranty,
            unit: dicoLabelsService.getUnit('day'),
            required: true,
            split: 3
          },
          {
            name: 'payment_timing',
            label: dicoLabelsService.getCollectionLabel('payment_timing', 'long'),
            type: 'dropdown',
            value: supplier.payment_timing,
            options: [
              { value: 'before_reception', label: 'Avant la livraison' },
              { value: 'at_reception', label: 'A la livraison' },
              { value: 'delay', label: 'A X jours' }
            ],
            required: true,
            afterChange: async (value: string, group) => {
              group.get('payment_delay').hidden = value !== 'delay';
            },
            split: 3
          },
          {
            name: 'payment_delay',
            label: dicoLabelsService.getCollectionLabel('payment_delay', 'long'),
            type: 'number',
            value: supplier.payment_delay,
            required: true,
            split: 3
          },
          {
            name: 'credit_amount',
            label: dicoLabelsService.getCollectionLabel('credit_amount', 'long'),
            type: 'text',
            value: `${supplier.credit_amount} ${supplier.currency?.toUpperCase() || ''}`,
            required: true
          },
          {
            name: 'comment',
            label: dicoLabelsService.getCollectionLabel('comment', 'long'),
            type: 'text-multiline',
            value: supplier.comment
          }
        ]
      }
    ]);
  }

  getSupplierContactForm(supplierContact: SupplierContact): Form<SupplierGrade> {
    return new Form<SupplierGrade>([
      {
        name: 'supplierContact',
        label: '',
        controls: [
          {
            name: 'name',
            label: dicoLabelsService.getCollectionLabel('name'),
            type: 'text',
            value: supplierContact?.name,
            required: true,
            split: 2
          },
          {
            name: 'email',
            label: dicoLabelsService.getCollectionLabel('email'),
            type: 'text',
            value: supplierContact?.email,
            required: true,
            split: 2
          }
        ]
      }
    ]);
  }

  getSupplierGradeForm(
    supplierGrade: SupplierGrade,
    supplierGradeList: SupplierGrade[],
    globalSettings: GlobalSettings
  ): Form<SupplierGrade> {
    return new Form<SupplierGrade>([
      {
        name: 'supplierGrade',
        label: '',
        controls: [
          {
            name: 'name',
            label: dicoLabelsService.getCollectionLabel('supplier_grade_name'),
            type: 'text',
            value: supplierGrade.name,
            required: true,
            split: 2
          },
          {
            name: 'supplierGradeParent',
            label: dicoLabelsService.getCollectionLabel('supplier_grade_parent'),
            value: supplierGrade.supplier_grade_parent_id,
            type: 'dropdown',
            options: [{ value: null, label: 'Aucun' }].concat(
              supplierGradeList.map(sg => {
                return { value: sg.id, label: sg.name };
              })
            ),
            split: 2
          },
          {
            name: 'purchase_reference',
            label: dicoLabelsService.getCollectionLabel('purchase_reference'),
            type: 'number',
            value: supplierGrade.purchase_reference,
            required: true,
            split: 2
          },
          {
            name: 'unique_purchase',
            label: dicoLabelsService.getCollectionLabel('unique_purchase'),
            type: 'toggle',
            value: supplierGrade.unique_purchase,
            split: 2
          },
          {
            name: 'min_battery_ratio',
            label: dicoLabelsService.getCollectionLabel('min_battery_ratio'),
            type: 'number',
            value: supplierGrade.min_battery_ratio,
            required: true,
            split: 1
          },
          {
            name: 'min_grade',
            label: dicoLabelsService.getCollectionLabel('min_grade'),
            type: 'options-list-unique',
            value: supplierGrade.min_grade,
            required: true,
            options: globalSettings.grades
              .filter(grade => grade[0] && grade[0] != 'A1')
              .map(grade => ({
                value: grade[0],
                label: dicoLabelsService.getCollectionLabel(grade[1])
              })),
            split: 1
          },
          {
            name: 'model',
            label: dicoLabelsService.getCollectionLabel('model', 'short'),
            type: 'dropdown',
            value: supplierGrade.model,
            required: true,
            options: [
              { value: 'MacBook', label: 'MacBook' },
              { value: 'iMac', label: 'iMac' },
              { value: 'Mac', label: 'Mac' },
              { value: 'other', label: 'Autres' }
            ],
            split: 1,
            afterChange: async (value: string, group) => {
              switch (value) {
                case 'MacBook':
                  group.get('small_failures').hidden = false;
                  group.get('small_failures').value = supplierGrade.small_failures;
                  group.get('screen').hidden = false;
                  group.get('screen').value = supplierGrade.screen;
                  group.get('topcase').hidden = false;
                  group.get('topcase').value = supplierGrade.topcase;
                  group.get('motherboard').hidden = false;
                  group.get('motherboard').value = supplierGrade.motherboard;
                  group.get('stikers').hidden = false;
                  group.get('stikers').value = supplierGrade.stikers;
                  group.get('miss_foot').hidden = true;
                  group.get('miss_foot').value = false;
                  group.get('stikers').split = 1;
                  break;
                case 'iMac':
                  group.get('small_failures').hidden = false;
                  group.get('small_failures').value = supplierGrade.small_failures;
                  group.get('screen').hidden = false;
                  group.get('screen').value = supplierGrade.screen;
                  group.get('topcase').hidden = false;
                  group.get('topcase').value = supplierGrade.topcase;
                  group.get('motherboard').hidden = false;
                  group.get('motherboard').value = supplierGrade.motherboard;
                  group.get('stikers').hidden = false;
                  group.get('stikers').value = supplierGrade.stikers;
                  group.get('miss_foot').hidden = false;
                  group.get('miss_foot').value = supplierGrade.miss_foot;
                  group.get('stikers').split = 2;
                  break;
                case 'Mac':
                  group.get('small_failures').hidden = false;
                  group.get('small_failures').value = supplierGrade.small_failures;
                  group.get('screen').hidden = true;
                  group.get('screen').value = false;
                  group.get('topcase').hidden = false;
                  group.get('topcase').value = supplierGrade.topcase;
                  group.get('motherboard').hidden = false;
                  group.get('motherboard').value = supplierGrade.motherboard;
                  group.get('stikers').hidden = false;
                  group.get('stikers').value = supplierGrade.stikers;
                  group.get('miss_foot').hidden = true;
                  group.get('miss_foot').value = false;
                  group.get('stikers').split = 1;
                  break;
                case 'other':
                  group.get('small_failures').hidden = true;
                  group.get('small_failures').value = false;
                  group.get('screen').hidden = true;
                  group.get('screen').value = false;
                  group.get('topcase').hidden = true;
                  group.get('topcase').value = false;
                  group.get('motherboard').hidden = true;
                  group.get('motherboard').value = false;
                  group.get('stikers').hidden = true;
                  group.get('stikers').value = false;
                  group.get('miss_foot').hidden = true;
                  group.get('miss_foot').value = false;
                  break;
              }
            }
          },
          {
            name: 'message',
            type: 'message-insert',
            content:
              "Les constats séléctionnée par défault sont : Clavier japonais, Blocage Logiciel (MDM, iCloud, Firmware), model, ram, taille de disque dur, taille d'écran, année, processeur, coeur, carte graphique",
            theme: 'light'
          },
          {
            name: 'small_failures',
            label: dicoLabelsService.getCollectionLabel('small_failures'),
            type: 'checkbox',
            value: supplierGrade.small_failures,
            split: 1
          },
          {
            name: 'screen',
            label: dicoLabelsService.getCollectionLabel('screen'),
            type: 'checkbox',
            value: supplierGrade.screen,
            split: 3
          },
          {
            name: 'topcase',
            label: dicoLabelsService.getCollectionLabel('topcase'),
            type: 'checkbox',
            value: supplierGrade.topcase,
            split: 3
          },
          {
            name: 'motherboard',
            label: dicoLabelsService.getCollectionLabel('motherboard'),
            type: 'checkbox',
            value: supplierGrade.motherboard,
            split: 3
          },
          {
            name: 'stikers',
            label: dicoLabelsService.getCollectionLabel('stikers'),
            type: 'checkbox',
            value: supplierGrade.stikers,
            split: 1
          },
          {
            name: 'miss_foot',
            label: dicoLabelsService.getCollectionLabel('miss_foot'),
            type: 'checkbox',
            value: supplierGrade.miss_foot,
            split: 2
          }
        ]
      }
    ]);
  }

  private getPickingCreationForm(picking: Picking): Form<Picking> {
    return new Form<Picking>([
      {
        name: 'picking-creation',
        label: '<i class="fas fa-cart"></i> Session(s) de picking',
        flat: true,
        controls: [
          {
            name: 'computer_type',
            label: dicoLabelsService.getCollectionLabel('computer_type'),
            type: 'dropdown',
            options: [
              { value: 'laptop', label: 'Ordinateurs portables' },
              { value: 'desktop', label: 'Ordinateurs fixes' }
            ],
            value: picking.computer_type,
            required: true,
            split: 2
          },
          {
            name: 'destination',
            label: dicoLabelsService.getCollectionLabel('destination'),
            type: 'dropdown',
            options: [
              { value: 'battery', label: 'Batterie' },
              { value: 'keys', label: 'Touches' },
              { value: 'packing', label: 'Emballage' }
            ],
            value: picking.destination,
            required: true,
            split: 2
          },
          {
            name: 'cart_number',
            label: 'Nb de chariot(s)',
            type: 'dropdown',
            options: Array.from({ length: 10 }, (_, i) => ({
              value: i + 1,
              label: `${i + 1}`
            })),
            value: picking.cart_number,
            required: true,
            split: 2
          },
          {
            name: 'computer_quantity',
            label: "Nb d'ordinateurs par chariot",
            type: 'dropdown',
            options: Array.from({ length: 40 }, (_, i) => ({
              value: i + 1,
              label: `${i + 1}`
            })),
            value: picking.computer_quantity,
            required: true,
            split: 2
          }
        ]
      }
    ]);
  }

  private getPickingForm(picking: Picking): Form<Picking> {
    return new Form<Picking>([
      {
        name: 'picking-creation',
        label: '<i class="fas fa-cart"></i> Session(s) de picking',
        flat: true,
        controls: [
          {
            name: 'computer_type',
            label: dicoLabelsService.getCollectionLabel('computer_type'),
            type: 'dropdown',
            options: [
              { value: 'laptop', label: 'Ordinateurs portables' },
              { value: 'desktop', label: 'Ordinateurs fixes' }
            ],
            value: picking.computer_type,
            disabled: true
          },
          {
            name: 'cart_number',
            label: 'Nb de chariot(s)',
            type: 'text',
            value: picking.cart_number,
            disabled: true,
            required: true,
            split: 2
          },
          {
            name: 'computer_quantity',
            label: "Nb d'ordinateurs par chariot",
            type: 'text',
            value: picking.computer_quantity,
            disabled: true,
            required: true,
            split: 2
          },
          {
            name: 'picked_quantity',
            label: "Nb d'ordinateurs sortis",
            type: 'text',
            value: picking.picked_quantity,
            disabled: true,
            required: true,
            split: 2
          }
        ]
      }
    ]);
  }

  private getTaskCreationForm(task: Task): Form<Task> {
    // ODOO compléter la liste des options de parts_code

    const options = [
      { label: 'Réparé', value: 'no_parts' },
      { label: 'Auto-réparation Apple', value: 'auto_apple' },
      { label: 'Ajustement charnière', value: 'adjust_hinge' },
      { label: 'Batterie', value: 'battery' },
      { label: "Boitier d'alimentation", value: 'power_supply' },
      { label: 'Extraction batterie', value: 'extract_battery' },
      { label: 'Extraction de la carte mère', value: 'extract_motherboard' },
      { label: 'Bezel', value: 'bezel' },
      { label: 'Bottom', value: 'bottom' },
      { label: "Capteur d'angle", value: 'angle_sensor' },
      { label: 'Carte fille', value: 'daughterboard' },
      { label: 'Carte mère', value: 'motherboard' },
      { label: 'Carte son', value: 'sound_card' },
      { label: 'Carte trackpad', value: 'trackpad_card' },
      { label: 'Charnières', value: 'hinge' },
      { label: 'Chassis', value: 'topcase' },
      { label: 'Clavier', value: 'keyboard' },
      { label: 'Cliker', value: 'cliker' },
      { label: 'Désactivation', value: 'disable' },
      { label: 'Disque dur', value: 'hard_disk' },
      { label: 'Extraction Écran', value: 'extract_screen' },
      { label: 'Hauts parleurs', value: 'speaker' },
      { label: 'Haut parleur Droit', value: 'speaker_right' },
      { label: 'Haut parleur Gauche', value: 'speaker_left' },
      { label: "Installation d'os", value: 'os' },
      { label: 'Jack', value: 'jack' },
      { label: 'Magsafe', value: 'magsafe_card' },
      { label: 'Microphone', value: 'micro' },
      { label: 'Nappe batterie', value: 'nappe_battery' },
      { label: 'Nappe carte fille', value: 'nappe_daughterboard' },
      { label: 'Nappe carte son', value: 'nappe_sound_card' },
      { label: 'Nappe clavier', value: 'nappe_keyboard' },
      { label: 'Nappe écran', value: 'nappe_screen' },
      { label: 'Nappe jack', value: 'nappe_jack' },
      { label: 'Nappe touchbar', value: 'nappe_touchbar' },
      { label: 'Nappe trackpad', value: 'nappe_trackpad' },
      { label: 'Pied', value: 'foot' },
      { label: 'RAM', value: 'ram' },
      { label: 'Rétroéclairage', value: 'backlight' },
      { label: 'Soudure', value: 'welding' },
      { label: 'Touchbar', value: 'touchbar' },
      { label: 'Touche', value: 'keys' },
      { label: 'Trackpad', value: 'trackpad' },
      { label: 'USB', value: 'usb' },
      { label: 'USB-C', value: 'usb_c' },
      { label: 'Ventilateur', value: 'fan' },
      { label: 'Webcam', value: 'webcam' },
      { label: 'Wifi', value: 'wifi' }
    ];
    return new Form<Task>([
      {
        name: 'task-creation',
        controls: [
          {
            name: 'parts_code',
            label: dicoLabelsService.getCollectionLabel('parts_code'),
            type: 'dropdown',
            value: task.parts_code,
            options: options,
            required: true,
            split: 1
          },
          {
            name: 'description',
            label: dicoLabelsService.getCollectionLabel('comment'),
            type: 'text-multiline',
            value: task.description,
            split: 1
          }
        ]
      }
    ]);
  }

  private buildSkuOkamac(
    source: Partial<Device> & Record<string, any>,
    globalSettings: GlobalSettings,
    grade?: string
  ): string {
    const modelCode = globalSettings.models.find(m => m[1] == source.model_namestring)?.[0] || 'XX';
    return [
      modelCode,
      source.size || 'XX',
      source.year || 'XXX',
      source.proc || 'XXX',
      Math.round(source.freq * 100) || 'XXX',
      source.number_cores?.toString()?.padStart(2, '0') || 'XX',
      source.graphic_card || 'XXX',
      source.hdtype?.toUpperCase() || 'XXX',
      source.hdsize?.toString()?.padStart(4, '0') || 'XXXX',
      'R',
      source.ram?.toString()?.padStart(3, '0') || 'XXX',
      source.color?.toUpperCase() || 'XX',
      // iPad devices store this SKU slot under 'cellular' instead of 'keyboard'
      (source.keyboard || source.cellular)?.toUpperCase() || 'XX',
      grade?.toUpperCase() || 'XX'
    ].join('');
  }

  private getDeviceModelControls(
    device: Device,
    type: DeviceFormType,
    globalSettings: GlobalSettings,
    updateSku?
  ): FormControlRaw<Device>[] {
    let init = true;
    const skuCreateControl = globalSettings.models.some(model => model[1] == device.model_namestring)
      ? this.buildSkuOkamac(device, globalSettings, device.grade)
      : null;
    let controls: FormControlRaw<Device>[] = [
      {
        name: 'message',
        type: 'message-insert',
        content: '',
        theme: '',
        hidden: true
      },
      {
        name: 'serial_no',
        label: dicoLabelsService.getCollectionLabel('serial_no', 'long'),
        type: 'text',
        value: device.serial_no,
        disabled:
          type === 'control-relocate' ||
          (type === 'control' && device.from_ajout_tool !== false && device?.control === undefined) ||
          type === 'device',
        required: true,
        afterChange: async (value: string, group) => {
          const exists = await devicesService.exist(value);
          if (exists) {
            const existingDevice = await devicesService.get(value);
            if (group.parent.get('message.message').hidden) group.parent.get('message.message').hidden = false;
            group.parent.get('message.message').theme = 'warning';
            group.parent.get('message.message').content =
              'Cet ordinateur a déjà été contrôlé le &nbsp;' +
              '<span class="emphasis">' +
              datesService.format(get(existingDevice, 'control.created_at')) +
              '</span>' +
              '</div>';
          } else {
            group.parent.get('message.message').hidden = true;
          }
          let messageSupplier = null;
          const supplierControl = group.parent.groups.filter(g => g.name == 'supplier')[0].controls;
          if (supplierControl.filter(g => g.name == 'message')[0])
            messageSupplier = group.parent.get('supplier.message');
          try {
            const result = await purchasesService.checkDevicePurchase('', value);
            const { status, message } = result;
            group.get('message').hidden = true;
            group.get('message').theme = status;
            group.get('message').content = message;
            group.get('message').hidden = false;
            if (status === 'success' && supplierControl.filter(g => g.name == 'check_ref_supplier')[0]) {
              group.parent.get('supplier.check_ref_supplier').hidden = true;
              if (supplierControl.filter(g => g.name == 'supplier_id')[0])
                group.parent.get('supplier.supplier_id').hidden = true;
              group.parent.get('supplier.reference_internal').hidden = true;
              if (supplierControl.filter(g => g.name == 'purchase_price')[0])
                group.parent.get('supplier.purchase_price').hidden = true;
              if (messageSupplier) messageSupplier.hidden = true;
            } else if (supplierControl.filter(g => g.name == 'check_ref_supplier')[0]) {
              group.parent.get('supplier.check_ref_supplier').hidden = false;
              if (init && device.device) {
                if (!device.supplier_reference) {
                  group.parent.get('supplier.check_ref_supplier').hidden = true;
                  group.parent.get('supplier.check_ref_supplier').value = true;
                }
              }
              messageSupplier.theme = status;
              messageSupplier.content = message;
              messageSupplier.hidden = false;
            } else {
              group.get('message').hidden = true;
            }
          } catch (error) {
            console.error(error);
            group.get('message').theme = 'error';
            group.get('message').content = 'Une erreur est survenue lors de la vérification du numéro de série.';
            group.get('message').hidden = false;
          }
          init = false;
        }
      },
      {
        name: 'sku_okamac',
        label: dicoLabelsService.getCollectionLabel('sku_okamac', 'long'),
        type: 'text',
        value: device.sku || device.supplier_sku || skuCreateControl,
        disabled: true
      },
      {
        name: 'model_identifier',
        label: dicoLabelsService.getCollectionLabel('model_identifier', 'long'),
        type: 'text',
        value: device.model_identifier,
        disabled:
          type === 'control-relocate' || (type === 'control' && device.from_ajout_tool !== false) || type === 'device',
        required: true,
        split: 2,
        afterChange: async (value: string, group) => {
          if (value) {
            // Only query on complete model identifiers like "MacBookPro18,3", not partial keystrokes
            if (!/^[A-Za-z]+\d+,\d+$/.test(value)) return;
            // Stale-request guard: discard responses from superseded keystrokes
            const requestId = Date.now();
            (group as any)._modelIdRequestId = requestId;
            try {
              const filters: Record<string, string> = { model: value };
              if (device.size) filters.size = String(device.size);
              const result = await referencesService.listCompatible(filters);
              if ((group as any)._modelIdRequestId !== requestId) return;
              const refs = result?.references || [];
              if (refs.length) {
                const uniqueModelStrings = [...new Set(refs.map(r => r.model_string).filter(Boolean))];
                const uniqueProcs = [...new Set(refs.map(r => r.proc).filter(Boolean))];
                const uniqueYears = [
                  ...new Set(refs.flatMap(r => (Array.isArray(r.year) ? r.year : [r.year]).filter(Boolean)))
                ];
                const uniqueColors = [...new Set(refs.map(r => r.color).filter(Boolean))];
                if (uniqueModelStrings.length === 1) {
                  group.get('model_namestring').value = uniqueModelStrings[0];
                }
                if (uniqueProcs.length) {
                  const procControl = group.parent.get('perfs.proc');
                  procControl.options = globalSettings.procs
                    .filter(p => uniqueProcs.includes(p[0]))
                    .map((proc, index) => ({ value: proc[0], label: proc[1], indexWeight: index }));
                  if (uniqueProcs.length === 1) procControl.value = uniqueProcs[0];
                  else if (!uniqueProcs.includes(procControl.value as string)) procControl.value = null;
                }
                if (uniqueYears.length) {
                  const yearControl = group.get('year');
                  yearControl.options = globalSettings.years
                    .filter(y => uniqueYears.includes(y[0]))
                    .map((year, index) => ({ value: year[0], label: year[1].display_name, indexWeight: index }));
                  if (uniqueYears.length === 1) yearControl.value = uniqueYears[0];
                  else if (!uniqueYears.includes(yearControl.value as string)) yearControl.value = null;
                }
                if (uniqueColors.length) {
                  const colorControl = group.get('color');
                  colorControl.options = globalSettings.colors
                    .filter(c => uniqueColors.includes(c[0]))
                    .map((color, index) => ({
                      value: color[0],
                      label: dicoLabelsService.decodeColor(color[0], color[1]),
                      indexWeight: index
                    }));
                  if (uniqueColors.length === 1) colorControl.value = uniqueColors[0];
                  else if (!uniqueColors.includes(colorControl.value as string)) colorControl.value = null;
                }
              }
            } catch (err) {
              console.error('model_identifier filter error:', err);
            }
          } else {
            group.parent.get('perfs.proc').options = globalSettings.procs.map((proc, index) => ({
              value: proc[0],
              label: proc[1],
              indexWeight: index
            }));
            group.get('year').options = globalSettings.years.map((year, index) => ({
              value: year[0],
              label: year[1].display_name,
              indexWeight: index
            }));
            group.get('color').options = globalSettings.colors.map((color, index) => ({
              value: color[0],
              label: dicoLabelsService.decodeColor(color[0], color[1]),
              indexWeight: index
            }));
          }
        }
      },
      {
        name: 'model_namestring',
        label: dicoLabelsService.getCollectionLabel('model_namestring', 'long'),
        type: 'dropdown',
        value: device.model_namestring,
        options: globalSettings.models.map(model => ({
          value: model[1],
          label: model[1]
        })),
        disabled:
          type === 'control-relocate' || (type === 'control' && device.from_ajout_tool !== false) || type === 'device',
        required: true,
        split: 2,
        afterChange: updateSku
      },
      {
        name: 'year',
        label: dicoLabelsService.getCollectionLabel('year', 'long'),
        type: 'dropdown',
        options: globalSettings.years.map((year, index) => ({
          value: year[0],
          label: year[1].display_name,
          indexWeight: index
        })),
        value: device.year,
        required: true,
        split: 2,
        afterChange: updateSku
      }
    ];
    if (type !== 'control' && type !== 'device-creation') {
      controls = [
        ...controls,
        {
          name: 'size',
          label: dicoLabelsService.getCollectionLabel('size', 'long'),
          type: 'dropdown',
          options: globalSettings.screens.map(size => ({
            value: parseFloat(size[1]),
            label: size[1] + dicoLabelsService.getUnit('screen_size')
          })),
          // Parse to match the number type of option values above; device.size may come back as a string from the API
          value: parseFloat(device.size as unknown as string),
          disabled: true,
          split: 2
        }
      ];
    }

    const isIpad = device.model_namestring?.split(' ')[0] === 'iPad';

    controls = [
      ...controls,
      {
        // Name must match the backend attribute (control.cellular vs control.keyboard) so the form submits to the right field.
        name: isIpad ? 'cellular' : 'keyboard',
        label: isIpad
          ? dicoLabelsService.getCollectionLabel('cellular', 'long')
          : dicoLabelsService.getCollectionLabel('keyboard', 'long'),
        type: 'dropdown',
        options: (isIpad ? globalSettings.cellular : globalSettings.keyboards).map((keyboard, index) => ({
          value: keyboard[0],
          label: keyboard[1],
          indexWeight: index
        })),
        value: isIpad ? device.cellular : device.keyboard,
        required: true,
        split: 2,
        afterChange: updateSku
      },
      {
        name: 'color',
        label: dicoLabelsService.getCollectionLabel('color', 'long'),
        type: 'dropdown',
        options: globalSettings.colors.map((color, index) => ({
          value: color[0],
          label: dicoLabelsService.decodeColor(color[0], color[1]),
          indexWeight: index
        })),
        value: device.color,
        required: true,
        split: 2,
        afterChange: updateSku
      },
      {
        name: 'keyboard_us',
        label: dicoLabelsService.getCollectionLabel('keyboard_us', 'long'),
        type: 'toggle',
        value: device.keyboard_us,
        split: 3
      },
      {
        name: 'nanotextured',
        label: dicoLabelsService.getCollectionLabel('nanotextured', 'long'),
        type: 'toggle',
        value: device.nanotextured ?? device.sku?.[1] === 'N',
        split: 3
      },
      {
        name: 'brand_new_box',
        label: dicoLabelsService.getCollectionLabel('brand_new_box'),
        type: 'toggle',
        value: device.brand_new_box,
        split: 3
      }
    ];
    // if (type === 'device') {
    //   controls = [
    //     ...controls,
    //     {
    //       name: 'swap_screen_todo',
    //       label: dicoLabelsService.getCollectionLabel('swap_screen_todo'),
    //       type: 'toggle',
    //       value: device.swap_screen_todo,
    //       split: 2
    //     }
    //   ];
    // }

    return controls;
  }

  private getDevicePerfsControls(
    device: Device,
    globalSettings: GlobalSettings,
    onSkuChange?
  ): FormControlRaw<Device>[] {
    if (!globalSettings) return [];
    // iPads (and similar non-Mac devices) have no graphic card / RAM spec to fill in
    const isIpad = device.model_namestring?.split(' ')[0] === 'iPad';
    return [
      {
        name: 'proc',
        label: dicoLabelsService.getCollectionLabel('proc', 'long'),
        type: 'dropdown',
        options: globalSettings.procs.map((proc, index) => ({
          value: proc[0],
          label: proc[1],
          indexWeight: index
        })),
        value: device.proc,
        required: true,
        split: 2,
        afterChange: async (value: string, group, hasChanged = true) => {
          if (value) {
            try {
              const modelIdentifier = group.parent?.get('model.model_identifier')?.value as string;
              const filters: Record<string, string> = { proc: value };
              if (modelIdentifier) filters.model = modelIdentifier;
              if (device.size) filters.size = String(device.size);
              const result = await referencesService.listCompatible(filters);
              const refs = result?.references || [];
              const uniqueFreqs = [
                ...new Set(refs.flatMap(r => (Array.isArray(r.freq) ? r.freq : [r.freq]).filter(Boolean)))
              ];
              const uniqueCores = [...new Set(refs.map(r => r.cores).filter(v => v != null))];
              // MacRef stores the code under graphic_card_code for some models (e.g. iPads); graphic_card is the fallback
              const uniqueGpus = [...new Set(refs.map(r => r.graphic_card_code || r.graphic_card).filter(Boolean))];
              if (uniqueFreqs.length) {
                const freqControl = group.get('freq');
                freqControl.options = uniqueFreqs.map(f => ({
                  value: f,
                  label: `${f} ${dicoLabelsService.getUnit('frequency')}`
                }));
                if (uniqueFreqs.length === 1) freqControl.value = uniqueFreqs[0];
                else if (!uniqueFreqs.includes(freqControl.value as string)) freqControl.value = null;
              }
              if (uniqueCores.length) {
                const coresControl = group.get('number_cores');
                coresControl.options = uniqueCores.map(c => ({ value: c, label: '' + c }));
                if (uniqueCores.length === 1) coresControl.value = uniqueCores[0];
                else if (!uniqueCores.includes(coresControl.value as number)) coresControl.value = null;
              }
              if (uniqueGpus.length) {
                const gpuControl = group.get('graphic_card');
                // MacRef may store the label instead of the code; resolve to code for SKU compatibility
                const resolvedGpus = uniqueGpus
                  .map(v => globalSettings.graphic_cards.find(gc => gc[0] === v || gc[1] === v))
                  .filter(Boolean);
                if (resolvedGpus.length) {
                  gpuControl.options = resolvedGpus.map((gc, i) => ({ value: gc[0], label: gc[1], indexWeight: i }));
                  if (resolvedGpus.length === 1) gpuControl.value = resolvedGpus[0][0];
                  else if (!resolvedGpus.some(gc => gc[0] === gpuControl.value)) gpuControl.value = null;
                }
              }
            } catch (err) {
              console.error('proc filter error:', err);
            }
          } else if (hasChanged !== false) {
            group.get('freq').options = globalSettings.hdd_freqs.map(f => ({
              value: f,
              label: `${f} ${dicoLabelsService.getUnit('frequency')}`
            }));
            group.get('number_cores').options = globalSettings.cores.map(c => ({ value: c, label: '' + c }));
            group.get('graphic_card').options = globalSettings.graphic_cards.map((code, i) => ({
              value: code[0],
              label: code[1],
              indexWeight: i
            }));
          }
          if (onSkuChange) onSkuChange(value, group);
        }
      },
      {
        name: 'freq',
        label: dicoLabelsService.getCollectionLabel('freq', 'long'),
        type: 'dropdown',
        options: globalSettings.hdd_freqs.map(freq => ({
          value: freq,
          label: `${freq} ${dicoLabelsService.getUnit('frequency')}`
        })),
        value: device.freq,
        required: true,
        split: 2,
        afterChange: onSkuChange
      },
      {
        name: 'number_cores',
        label: dicoLabelsService.getCollectionLabel('number_cores', 'long'),
        type: 'dropdown',
        options: globalSettings.cores.map(core => ({
          value: core,
          label: '' + core
        })),
        value: device.number_cores,
        required: true,
        split: 2,
        afterChange: onSkuChange
      },
      {
        name: 'graphic_card',
        label: dicoLabelsService.getCollectionLabel('graphic_card', 'long'),
        type: 'dropdown',
        options: globalSettings.graphic_cards.map((code, index) => ({
          value: code[0],
          label: code[1],
          indexWeight: index
        })),
        value: device.graphic_card,
        required: !isIpad,
        split: 2,
        afterChange: onSkuChange
      },
      {
        name: 'hdtype',
        label: dicoLabelsService.getCollectionLabel('hdtype', 'long'),
        type: 'dropdown',
        options: globalSettings.drives.map((drive, index) => ({
          value: drive[0],
          label: drive[1],
          indexWeight: index
        })),
        value: device.hdtype,
        required: true,
        split: 2,
        afterChange: onSkuChange
      },
      {
        name: 'hdsize',
        label: dicoLabelsService.getCollectionLabel('hdsize', 'long'),
        type: 'dropdown',
        options: globalSettings.hdd_sizes.map(size => ({
          value: size,
          label: `${size} ${dicoLabelsService.getUnit('capacity')}`
        })),
        value: device.hdsize,
        required: true,
        split: 2,
        afterChange: onSkuChange
      },
      {
        name: 'ram',
        label: dicoLabelsService.getCollectionLabel('ram', 'long'),
        type: 'dropdown',
        options: globalSettings.rams.map(ram => ({
          value: ram,
          label: `${ram} ${dicoLabelsService.getUnit('capacity')}`
        })),
        unit: dicoLabelsService.getUnit('capacity'),
        value: device.ram,
        required: !isIpad,
        split: 2,
        afterChange: onSkuChange
      }
    ];
  }

  private getDeviceBatteryControls(device: Device): FormControlRaw<Device>[] {
    const hasBattery = !/(i\s?mac)|(mac\s?mini)|(mac\s?pro)/i.test(device.model_namestring);

    const controls: FormControlRaw<Device>[] = [
      {
        name: 'battery_ratio',
        label: dicoLabelsService.getCollectionLabel('battery_ratio', 'long'),
        type: 'number',
        unit: dicoLabelsService.getUnit('percentage'),
        value: device.battery_ratio,
        disabled: true,
        split: 3
      },
      {
        name: 'current_battery_capacity',
        label: dicoLabelsService.getCollectionLabel('current_battery_capacity', 'long'),
        type: 'number',
        unit: dicoLabelsService.getUnit('intensity'),
        value: device.current_battery_capacity,
        required: hasBattery,
        split: 3,
        afterChange: async (value: number, group) => {
          const ratio = Math.round((value / Number(group.get('initial_battery_capacity').value)) * 100);
          group.get('battery_ratio').value = ratio > 100 ? 100 : ratio;
        }
      },
      {
        name: 'initial_battery_capacity',
        label: dicoLabelsService.getCollectionLabel('initial_battery_capacity', 'long'),
        type: 'number',
        unit: dicoLabelsService.getUnit('intensity'),
        value: device.initial_battery_capacity,
        required: hasBattery,
        split: 3,
        afterChange: async (value: number, group) => {
          const ratio = Math.round((Number(group.get('current_battery_capacity').value) / value) * 100);
          group.get('battery_ratio').value = ratio > 100 ? 100 : ratio;
        }
      },
      {
        name: 'cycles',
        label: dicoLabelsService.getCollectionLabel('cycles', 'long'),
        type: 'number',
        value: device.cycles,
        required: hasBattery,
        split: 2
      }
    ];

    return controls;
  }

  // private getDeviceComponentsControls(
  //   faulty: FaultyComponent[],
  //   globalSettings: GlobalSettings
  // ): FormControlRaw<{ faulty: FaultyComponent[] }>[] {
  //   return [
  //     {
  //       name: 'faulty',
  //       label: dicoLabelsService.getCollectionLabel('faulty', 'long'),
  //       type: 'switch-list',
  //       value: faulty,
  //       options: globalSettings.components.map((component, index) => ({
  //         value: component[0],
  //         label: component[1],
  //         indexWeight: index
  //       }))
  //     }
  //   ];
  // }
  private getDeviceSupplierControls(
    device: Device,
    type: DeviceFormType,
    globalSettings: GlobalSettings
  ): FormControlRaw<Device>[] {
    if (!globalSettings) return [];
    if (type == 'device') {
      return [
        {
          name: 'purchase_price',
          label: dicoLabelsService.getCollectionLabel('purchase_price', 'long'),
          type: 'number',
          value: device.purchase_price,
          unit: dicoLabelsService.getUnit('money'),
          required: true,
          step: '0.01',
          split: 3
        },
        {
          name: 'discount',
          label: dicoLabelsService.getCollectionLabel('discount'),
          type: 'number',
          value: device.discount,
          unit: dicoLabelsService.getUnit('money'),
          required: false,
          step: '0.01',
          split: 3
        },
        {
          name: 'purchase_date',
          label: dicoLabelsService.getCollectionLabel('purchase_date', 'long'),
          type: 'datetime-local',
          value: device.purchase_date,
          required: true,
          split: 3
        },
        {
          name: 'supplier_id',
          label: dicoLabelsService.getCollectionLabel('supplier_id', 'long'),
          type: 'dropdown',
          value: device.supplier_id,
          options: globalSettings.suppliers.map(supplier => ({
            value: supplier[0],
            label: supplier[1].display_name,
            indexWeight: supplier[1].index_weight
          })),
          required: true,
          split: 2
        },
        {
          name: 'supplier_reference',
          label: dicoLabelsService.getCollectionLabel('supplier_reference', 'long'),
          type: 'text',
          value: device.supplier_reference,
          split: 2
        },
        {
          name: 'tax_scheme',
          label: dicoLabelsService.getCollectionLabel('tax_scheme', 'long'),
          type: 'dropdown',
          options: globalSettings.schemes.map((scheme, index) => ({
            value: scheme[0],
            label: scheme[1],
            indexWeight: index
          })),
          value: device.tax_scheme,
          required: true,
          split: 2
        },
        {
          name: 'warranty',
          label: dicoLabelsService.getCollectionLabel('warranty', 'long'),
          type: 'number',
          value: device.warranty,
          unit: dicoLabelsService.getUnit('day'),
          required: true,
          split: 2
        }
      ];
    }

    let check_ref_supplier = true;
    let initialize = true;
    if (device.device) {
      if (!device.supplier_reference) {
        check_ref_supplier = false;
      }
    }

    const showMessage = (control, message: string, theme: string) => {
      control.hidden = true;
      control.content = message;
      control.theme = theme;
      control.hidden = false;
    };

    const handleBuybackCheck = async (mpBuybackId: string, group) => {
      const messageControl = group.get('message');
      const messageModelControl = group.parent.get('model.message');
      if (!mpBuybackId) {
        messageControl.hidden = true;
        return;
      }
      const result = await buybackService.checkBuyback(mpBuybackId);
      showMessage(messageControl, result.message, result.status);
      if (result.status === 'success') {
        messageModelControl.hidden = true;
      } else if (messageModelControl.content) {
        messageModelControl.hidden = true;
        messageModelControl.hidden = false;
      }
    };

    return [
      {
        name: 'message',
        type: 'message-insert',
        content: '',
        theme: '',
        hidden: true
      },
      {
        name: 'buyback',
        label: dicoLabelsService.getCollectionLabel('buyback'),
        type: 'toggle',
        value: false,
        afterChange: async (value: boolean, group) => {
          const checkRefSupplierControl = group.get('check_ref_supplier');
          const referenceInternalControl = group.get('reference_internal');
          const supplierReference = group.get('supplier_reference');
          if (value) {
            checkRefSupplierControl.hidden = true;
            referenceInternalControl.hidden = true;
            supplierReference.hidden = false;
            await handleBuybackCheck(supplierReference.value as string, group);
          } else {
            checkRefSupplierControl.hidden = false;
            group.get('message').hidden = true;
          }
        },
        split: 2
      },
      {
        name: 'check_ref_supplier',
        label: dicoLabelsService.getCollectionLabel('check_ref_supplier'),
        type: 'toggle',
        value: check_ref_supplier,
        afterChange: async (value: boolean, group) => {
          const messageControl = group.get('message');
          const messageModelControl = group.parent.get('model.message');
          const serial_number = group.parent.get('model.serial_no').value as string;
          const referenceInternalControl = group.get('reference_internal');
          const supplierReference = group.get('supplier_reference');
          if (value) {
            if (!initialize) supplierReference.value = null;
          } else {
            messageControl.content = 'Sélectionner une référence interne';
            messageControl.theme = 'warning';
            if (messageModelControl.content) messageModelControl.hidden = false;
            if (messageControl.content) {
              messageControl.hidden = true;
              messageControl.hidden = false;
            }
            if (!initialize) referenceInternalControl.value = null;
          }
          const result = await purchasesService.checkDevicePurchase('ref', serial_number);
          supplierReference.hidden = !value;
          referenceInternalControl.hidden = value;
          const referencesInternes: string[] = result.references_internes || [];
          referenceInternalControl.options = referencesInternes.map(reference => ({
            value: reference,
            label: reference
          }));
        },
        split: 2
      },
      {
        name: 'supplier_reference',
        label: dicoLabelsService.getCollectionLabel('supplier_reference', 'long'),
        type: 'text',
        value: device.supplier_reference,
        split: 2,
        afterChange: async (value: string, group) => {
          group.get('buyback').loading = true;
          group.change$.next();
          if (group.get('buyback').value) {
            await handleBuybackCheck(value, group);
            group.get('buyback').loading = false;
            group.change$.next();
            return;
          }
          const messageControl = group.get('message');
          const messageModelControl = group.parent.get('model.message');
          const serial_number = group.parent.get('model.serial_no').value as string;
          const referenceInternalControl = group.get('reference_internal');
          const result = await purchasesService.checkDevicePurchase(value, serial_number);
          if (!initialize) referenceInternalControl.value = null;
          referenceInternalControl.hidden = !value;
          if ((messageModelControl.theme == 'success' && value) || messageModelControl.theme == 'warning') {
            showMessage(messageControl, result.message, result.status);
          }
          if (value) {
            if (messageModelControl.content && messageModelControl.theme != 'success') {
              messageModelControl.hidden = result.status == 'success';
            }
            if (result.status != 'success') {
              const referencesInternes: string[] = result.references_internes || [];
              referenceInternalControl.options = referencesInternes.map(reference => ({
                value: reference,
                label: reference
              }));
            } else {
              referenceInternalControl.hidden = true;
            }
          } else {
            if (messageModelControl.theme == 'success') {
              messageControl.hidden = true;
            } else {
              if (messageModelControl.content) messageModelControl.hidden = false;
            }
          }
          if (initialize && result.status == 'success') initialize = false;
          group.get('buyback').loading = false;
          group.change$.next();
        }
      },
      {
        name: 'reference_internal',
        type: 'option-list',
        label: 'Associer manuellement à une liste',
        value: device.reference_internal,
        options: [],
        hidden: true,
        afterChange: async (selectedOption: string, group) => {
          if (selectedOption) {
            group.get('message').hidden = true;
            group.parent.get('model.message').hidden = true;
          }
          initialize = false;
        }
      }
    ];
  }

  private getDeviceGradingsControls(device: Device, type: DeviceFormType): FormControlRaw<Device>[] {
    const gradeForm: FormControlRaw<Device>[] = [];
    if (type == 'control' || type == 'control-relocate') {
      const grading = device.grading;
      ['color_spots', 'white_dark_spots', 'liquid_spots', 'dust_screen', 'dead_pixel'].forEach(element => {
        let value = null;
        if (grading) value = grading[element];

        gradeForm.push({
          name: element,
          label: dicoLabelsService.getCollectionLabel(element),
          type: 'options-list-unique',
          value: value,
          // dead_pixel stores a count, not a severity: 1 pixel is weighted, several triggers a screen extraction
          options:
            element === 'dead_pixel'
              ? [
                  { value: 1, label: '1' },
                  { value: 2, label: 'Plusieurs' }
                ]
              : [
                  { value: 1, label: dicoLabelsService.getCollectionLabel('light') },
                  { value: 2, label: 'Invendable' }
                ],
          split: 2
        });
      });
    }
    return gradeForm;
  }

  private getFilterOptions(name: string, values: any[], globalSettings: GlobalSettings): FormControlOption[] {
    let unit = '';
    switch (name) {
      case 'freq':
        unit = dicoLabelsService.getUnit('frequency');
        break;
      case 'hdsize':
      case 'ram':
        unit = dicoLabelsService.getUnit('capacity');
        break;
    }
    if (name === 'order_line_type') {
      const filteredValues = values.filter(v => v !== 'accessory_standalone');
      const opts = this.getSortedOptions(globalSettings?.order_line_types, filteredValues);
      if (values.includes('accessory_standalone')) {
        opts.splice(opts.length - 1, 0, { value: 'accessory_standalone', label: 'Accessoires (hors bundle)' });
      }
      return opts;
    }
    if (name === 'return_type') return this.getSortedOptions(globalSettings?.return_types, values);
    if (name === 'year' || name === 'ordered_year') return this.getSortedOptions(globalSettings?.years, values);
    if (name === 'color') return this.getSortedOptions(globalSettings?.colors, values);
    if (name === 'model') return this.getSortedOptions(globalSettings?.models, values);
    if (name === 'graphic_card') return this.getSortedOptions(globalSettings?.graphic_cards, values);
    if (name === 'freq') return this.getSortedOptions(globalSettings?.hdd_freqs, values, unit);
    if (name === 'proc') return this.getSortedOptions(globalSettings?.procs, values);
    if (name === 'ram') return this.getSortedOptions(globalSettings?.rams, values, unit);
    if (name === 'hdsize') return this.getSortedOptions(globalSettings?.hdd_sizes, values, unit);
    if (name === 'grade')
      return this.getSortedOptions(
        globalSettings?.grades.filter(grade => grade[0] !== 'A1'),
        values
      );
    if (name === 'keyboard') return this.getSortedOptions(globalSettings?.keyboards, values);
    if (name === 'stock_status_id') return this.getSortedOptions(globalSettings?.stocks_status, values);
    if (name === 'tax_scheme') return this.getSortedOptions(globalSettings?.schemes, values);
    if (name === 'buffer_alert') return this.getSortedOptions(globalSettings?.buffer_alerts, values);
    if (name === 'supplier') return this.getSortedOptions(globalSettings?.suppliers, values);
    if (name === 'spares_mix' || name === 'sub_category')
      return this.getSortedOptions(globalSettings?.spare_types, values);
    if (name === 'consumables_mix') return this.getSortedOptions(globalSettings?.consumable_types, values);
    if (name === 'devices_mix') return this.getSortedOptions(globalSettings?.models, values);
    if (name === 'country') return this.getSortedOptions(globalSettings?.countries, values);
    if (name === 'computer_type') return this.getSortedOptions(globalSettings?.computer_types, values);
    // if (name === 'declared_faulty') return this.getSortedOptions(globalSettings?.components, values);
    if (name === 'reasons') return this.getSortedOptions(globalSettings?.customer_declared_faulties_only_keys, values);
    if (name === 'state') return this.getSortedOptions(globalSettings?.states, values);
    if (name === 'process_handled_by') {
      return values.concat([{ value: 'all', label: 'Tous' }]);
    }

    if (name === 'customer_type') {
      const labels: Record<string, string> = {
        individual: 'Particulier',
        professional: 'Professionnel',
        association: 'Association',
        client: 'Client',
        bloqué: 'Bloqué',
        'Mauvais payeur': 'Mauvais payeur',
        prospect: 'Prospect',
        archived: 'Archivé'
      };
      return values.map(opt => ({ value: opt, label: labels[opt] || opt })).concat([{ value: 'all', label: 'Tous' }]);
    }

    if (name === 'negative_balance' || name === 'overdue_invoice') {
      return values
        .map(opt => {
          if (opt === true) return { value: opt, label: 'Oui' };
          if (opt === false) return { value: opt, label: 'Non' };
          return { value: opt, label: String(opt) };
        })
        .concat([{ value: 'all', label: 'Tous' }]);
    }

    if (name === 'payment_method') {
      const pmLabels: Record<string, string> = {
        qonto: 'Qonto',
        paypal: 'PayPal',
        payplug: 'Payplug',
        marketplace: 'Marketplace',
        credit_note: 'Avoir'
      };
      return values.map(opt => ({ value: opt, label: pmLabels[opt] || opt })).concat([{ value: 'all', label: 'Tous' }]);
    }

    if (name === 'purchase_status') {
      return values
        .map(opt => ({ value: opt, label: PURCHASE_STATUS_LABELS[opt] || opt }))
        .concat([{ value: 'all', label: 'Tous' }]);
    }

    if (name === 'ordered_size') {
      return (globalSettings?.screens as any)
        .map(setting => {
          return { value: setting[0], label: setting[1] + '"' };
        })
        .concat([{ value: 'all', label: 'Tous' }]);
    }

    return values
      .map(opt => {
        if (opt === null || opt === undefined) return { value: opt, label: 'Vide' };
        else if (opt === false) return { value: opt, label: 'Non' };
        else if (opt === true) return { value: opt, label: 'Oui' };
        else if (name === 'size') return { value: opt, label: opt + '"' };
        else return { value: opt, label: opt + (unit ? ` ${unit}` : '') };
      })
      .concat([{ value: 'all', label: 'Tous' }]);
  }

  private getSortedOptions(
    settings: GlobalSettings[keyof GlobalSettings],
    options: (string | number | boolean)[],
    unit?: string
  ): FormControlOption[] {
    if (!settings) return [];

    return (settings as any) // As any to avoid 'Expression not callable' TS error o_O
      .filter(setting =>
        setting instanceof Array
          ? options.findIndex(opt => opt + '' === setting[0] + '') > -1
          : options.findIndex(opt => opt + '' === setting + '') > -1
      )
      .map(setting => {
        const value = setting instanceof Array ? setting[0] : setting;
        const label =
          setting instanceof Array ? (setting[1] instanceof Object ? setting[1].display_name : setting[1]) : setting;
        return { value, label: label + (unit ? ` ${unit}` : '') };
      })
      .concat([{ value: 'all', label: 'Tous' }]);
  }
}

export const formsService = new FormsService();
